import React, { useState, useEffect, useRef } from 'react';
import { useModalA11y } from '../hooks/use-modal-a11y';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Modal } from '../components/ui/modal';
import { useAuth } from '../hooks/use-auth';
import { useOrderDetail, useReprocessOrder, useRestartWorkflow, useRefreshTemporalStatus, useTemporalWorkflowDetail, usePauseOrder, useResumeOrder, useAdminAdvanceWorkflow, useDeleteOrder } from '../hooks/use-orders';
import { useUserApprovals } from '../hooks/use-user-approvals';
import { useSubmitApproval, useSubmitBatchApproval, useCheckRequiredFields } from '../hooks/use-approvals';
import { OrderDetailDto, ApprovalDecisionRequest } from '../types';
import { formatCurrency, formatDate, formatSkuDisplay } from '../utils';
import { RejectionReasonModal } from '../components/rejection-reason-modal';
import { PauseReasonModal } from '../components/pause-reason-modal';
import { QuantityModificationModal } from '../components/quantity-modification-modal';
import { OrderAttributesSection } from '../components/order-attributes-section';
import { ApprovalFlowModal, OrderAuditModal, AddApproversModal, OrderActivityLog, PickupSignatureModal } from '../components/orders';
import { PickListModal } from '../components/pick-list-modal';
import { useDivisionWorkflow } from '../hooks/use-division-workflows';
import { useOrderAuditReport } from '../hooks/use-order-audit-report';
import { useOrderReviewDrafts, useUpsertOrderReviewDraft, useDeleteOrderReviewDraft, useDeleteAllOrderReviewDrafts } from '../hooks/use-order-review-drafts';
import { useDivision } from '../hooks/use-divisions';

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isAdmin, isWorkflowAdmin, roles = [] } = useAuth();
  const isSupport = roles.includes('approvn-customer-support');
  // WorkflowAdmin should have the same order-level access as DivisionAdmin (both covered by
  // isAdmin/isWorkflowAdmin here) - EXCEPT deleting orders, which stays Admin/DivisionAdmin only.
  const isOrderAdmin = isAdmin || isWorkflowAdmin;
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'ship-to': true,
    'order-summary': true,
    'notes': true,
    'order-approval': true,
  });
  const [lineItemStatuses, setLineItemStatuses] = useState<Record<string, 'approved' | 'rejected' | null>>({});

  // Draft decisions for steps with RequireExplicitSubmit (drafted locally, not sent to the
  // workflow engine until Submit Review - see order-approval-review-draft repo memory note).
  const [draftDecisions, setDraftDecisions] = useState<Record<string, {
    decision: 'accept' | 'decline';
    comments?: string;
    approvedQuantity?: number;
    modificationReason?: string;
    signatureData?: string;
  }>>({});
  const [showDraftSummary, setShowDraftSummary] = useState(false);
  
  // Track if we're in the middle of processing an approval (including delay and refetch)
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  
  // Track polling state after batch approval
  const [isPollingForStepChange, setIsPollingForStepChange] = useState(false);
  const [pollingStartStep, setPollingStartStep] = useState<number | null>(null);
  const [pollingStartPendingCount, setPollingStartPendingCount] = useState<number | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldStopPollingRef = useRef(false);
  
  // State for rejection modal
  const [rejectionModal, setRejectionModal] = useState<{
    isOpen: boolean;
    lineItemId?: string;
    lineItemName?: string;
    lineItemLevel?: number;
    isBulkDecline?: boolean; // Flag to indicate declining all items
  }>({
    isOpen: false
  });

  // State for pause reason modal
  const [pauseReasonModal, setPauseReasonModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });

  // State for quantity modification modal
  const [modificationModal, setModificationModal] = useState<{
    isOpen: boolean;
    lineItemId?: string;
    lineItemName?: string;
    lineItemLevel?: number;
    originalRequestedQuantity?: number; // Original quantity from order request
    currentApprovedQuantity?: number; // Most recent approved quantity
    unitOfMeasure?: string;
  }>({
    isOpen: false
  });

  // State for required fields validation error modal
  const [requiredFieldsModal, setRequiredFieldsModal] = useState<{
    isOpen: boolean;
    missingFields?: string[];
    message?: string;
  }>({
    isOpen: false
  });

  // State for approval flow modal
  const [showApprovalFlow, setShowApprovalFlow] = useState(false);
  
  // State for pick list modal
  const [showPickList, setShowPickList] = useState(false);
  
  // State for raw data modal
  const [showRawData, setShowRawData] = useState(false);
  const { dialogRef: rawDataDialogRef } = useModalA11y<HTMLDivElement>(showRawData, () => setShowRawData(false));

  // State for audit report modal
  const [showAuditReport, setShowAuditReport] = useState(false);

  // State for inline admin activity log
  const [showActivityLog, setShowActivityLog] = useState(false);

  // State for add approvers modal
  const [showAddApprovers, setShowAddApprovers] = useState(false);

  // State for bulk approval confirmation modal
  const [showBulkApprovalModal, setShowBulkApprovalModal] = useState(false);
  const [bulkApprovalComments, setBulkApprovalComments] = useState('');

  // State for the pickup signature confirmation modal (Confirm Pickup steps that require a signature)
  // approvedQuantity/modificationReason carry a pending quantity edit through to the signed submit
  // (see openPickupSignatureFlow), since a signature-required step can't submit that edit on its own.
  const [pickupSignatureModal, setPickupSignatureModal] = useState<{
    isOpen: boolean;
    isBulk?: boolean;
    lineItemId?: string;
    approvedQuantity?: number;
    modificationReason?: string;
  }>({ isOpen: false });
  const [isSubmittingPickupSignature, setIsSubmittingPickupSignature] = useState(false);
  // Signature captured for the current step, reused for subsequent items so the approver
  // isn't prompted to sign again for every line item / the bulk action within the same step.
  const [capturedSignature, setCapturedSignature] = useState<{ step: number; signatureData: string } | null>(null);

  // Get division ID and user approvals from hook
  const { divisionId, data: userApprovals, isLoading: isLoadingApprovals } = useUserApprovals();

  // Fetch order detail
  const { 
    data: orderDetail, 
    isLoading, 
    error, 
    refetch 
  } = useOrderDetail(divisionId || '', orderId || '', !!divisionId && !!orderId);

  // Track the workflow step the local optimistic line item statuses were recorded for.
  // Without this, once a user approves an item their status is cached locally forever,
  // so if that same user is assigned as an approver on a LATER step too (multi-level
  // approvals), the item would incorrectly keep showing as "approved" with no action
  // buttons once the order advances back around to a step where they need to act again.
  const lineItemStatusesStepRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!orderDetail) return;
    if (lineItemStatusesStepRef.current !== undefined &&
        lineItemStatusesStepRef.current !== orderDetail.currentStep) {
      setLineItemStatuses({});
    }
    lineItemStatusesStepRef.current = orderDetail.currentStep;
  }, [orderDetail?.currentStep]);

  // Drafted decisions only make sense for the step they were staged at - clear them if the
  // order moves to a different step out from under the current view (e.g. another approver
  // already submitted enough decisions to advance the workflow).
  const draftDecisionsStepRef = useRef<number | undefined>(undefined);
  // Snapshot of the step the reviewer was on when they started drafting, checked again at
  // Submit Review time so a concurrent change (another approver/admin action moved the order
  // past this step) is caught before submitting now-stale decisions.
  const reviewSnapshotStepRef = useRef<number | null>(null);
  useEffect(() => {
    if (!orderDetail) return;
    if (draftDecisionsStepRef.current !== undefined &&
        draftDecisionsStepRef.current !== orderDetail.currentStep) {
      setDraftDecisions({});
      reviewSnapshotStepRef.current = null;
    }
    draftDecisionsStepRef.current = orderDetail.currentStep;
  }, [orderDetail?.currentStep]);

  // Warn before navigating away with unsubmitted draft decisions so a refresh/closed tab
  // doesn't silently lose a reviewer's in-progress work.
  useEffect(() => {
    const hasDrafts = Object.keys(draftDecisions).length > 0;
    if (!hasDrafts) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [draftDecisions]);

  // Server-persisted drafts so an in-progress review survives a refresh/closed tab - only
  // fetched for RequireExplicitSubmit steps. Hydrated into local draftDecisions once per step
  // (not on every refetch) so it never clobbers decisions staged later in the same session.
  const { data: serverReviewDrafts } = useOrderReviewDrafts(
    divisionId || '',
    orderId || '',
    !!divisionId && !!orderId && !!orderDetail?.currentStepRequireExplicitSubmit
  );
  const draftsHydratedStepRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!orderDetail?.currentStepRequireExplicitSubmit || !serverReviewDrafts) return;
    if (draftsHydratedStepRef.current === orderDetail.currentStep) return;
    draftsHydratedStepRef.current = orderDetail.currentStep;
    const forThisStep = serverReviewDrafts.filter(d => d.step === orderDetail.currentStep);
    if (forThisStep.length === 0) return;
    setDraftDecisions(prev => {
      const next = { ...prev };
      forThisStep.forEach(d => {
        next[d.lineItemId] = {
          decision: d.decision,
          comments: d.comments,
          approvedQuantity: d.approvedQuantity,
          modificationReason: d.modificationReason,
          signatureData: d.signatureData
        };
      });
      return next;
    });
  }, [serverReviewDrafts, orderDetail?.currentStep, orderDetail?.currentStepRequireExplicitSubmit]);

  // Fetch workflow details if order has a workflowId
  const { 
    data: workflowDetail 
  } = useDivisionWorkflow(
    divisionId || '',
    orderDetail?.workflowId || '',
    !!divisionId && !!orderDetail?.workflowId
  );

  // Fetch division settings to get default pause timeout
  const {
    data: divisionData
  } = useDivision(divisionId || '', !!divisionId);

  // Fetch audit report data (only when modal is opened)
  const { 
    data: auditReport, 
    isLoading: isLoadingAuditReport 
  } = useOrderAuditReport(
    divisionId || '',
    orderId || '',
    showAuditReport && !!divisionId && !!orderId
  );

  // Helper function to check if order is in progress
  const isOrderInProgress = (status: string | undefined) => {
    if (!status) return false;
    const statusLower = status.toLowerCase();
    return statusLower === 'processing' || 
           statusLower === 'pending' || 
           statusLower === 'submitted' ||
           statusLower === 'workflowstarted' ||
           statusLower === 'error' ||
           statusLower === 'partiallycompleted';
  };

  // Helper function to check if the current user has admin-level EDIT access to orders (Admin/
  // DivisionAdmin only - WorkflowAdmin can view all orders but not act as an admin approver/editor).
  const isUserAdmin = () => isAdmin;

  // Helper function to get the current step type (e.g., "Review", "Approval", "Picker")
  const getCurrentStepType = () => {
    if (!orderDetail || !orderDetail.stepNames || !orderDetail.currentStep) {
      return 'Approval'; // Default fallback
    }
    return orderDetail.stepNames[orderDetail.currentStep] || 'Approval';
  };

  // Helper function to get action verb based on step type
  const getStepActionVerb = (capitalize = true) => {
    // Steps whose actual stepType is "fulfillment" or "approval" always use their
    // dedicated verbiage ("pick" / "approve"), regardless of how the step is named.
    const actualStepType = orderDetail?.stepTypes?.[orderDetail.currentStep]?.toLowerCase();
    if (actualStepType === 'fulfillment') {
      return capitalize ? 'Pick' : 'pick';
    }
    if (actualStepType === 'approval') {
      return capitalize ? 'Approve' : 'approve';
    }
    if (actualStepType === 'confirmation') {
      return capitalize ? 'Confirm Pickup' : 'confirm pickup';
    }

    const stepType = getCurrentStepType().toLowerCase();
    
    // Map step types to appropriate verbs
    const verbMap: Record<string, string> = {
      'review': 'review',
      'reviewer': 'review',
      'approval': 'accept',
      'approver': 'accept',
      'picker': 'pick',
      'picking': 'pick',
      'buyer': 'accept',
      'purchasing': 'accept'
    };
    
    const verb = verbMap[stepType] || 'accept'; // Default to 'accept'
    return capitalize ? verb.charAt(0).toUpperCase() + verb.slice(1) : verb;
  };

  // Helper function to get the "Decline" action label based on step type.
  // Confirm Pickup steps use "Decline Pickup" instead of the generic "Decline".
  const getDeclineActionLabel = () => {
    const actualStepType = orderDetail?.stepTypes?.[orderDetail.currentStep]?.toLowerCase();
    if (actualStepType === 'confirmation') {
      return 'Decline Pickup';
    }
    return 'Decline';
  };

  // Pick (fulfillment) and Confirm Pickup (confirmation) steps don't show vendor info in the item grid.
  const shouldHideVendorColumn = () => {
    const actualStepType = orderDetail?.stepTypes?.[orderDetail.currentStep]?.toLowerCase();
    return actualStepType === 'fulfillment' || actualStepType === 'confirmation';
  };

  const normalizeEmail = (email?: string | null) => (email || '').trim().toLowerCase();

  const isCurrentUserInPendingApprovers = (userEmail?: string) => {
    if (!orderDetail || !userEmail) return false;
    const normalizedUserEmail = normalizeEmail(userEmail);
    return (orderDetail.pendingApprovers || []).some(
      email => normalizeEmail(email) === normalizedUserEmail
    );
  };

  const canCurrentUserActOnLineItem = (item: OrderDetailDto['lineItems'][0], userEmail?: string) => {
    if (!orderDetail || !userEmail) return false;
    const currentStepStatuses = item.stepStatuses?.[orderDetail.currentStep];
    if (!currentStepStatuses || currentStepStatuses.length === 0) {
      // Fallback for older/partial payloads where stepStatuses may be missing.
      return isCurrentUserInPendingApprovers(userEmail);
    }

    const normalizedUserEmail = normalizeEmail(userEmail);
    return currentStepStatuses.some(status =>
      normalizeEmail(status.approverEmail) === normalizedUserEmail &&
      status.status?.toLowerCase() === 'pending'
    );
  };

  // Helper function to check whether a line item's approval is actually finalized
  // at the current step (i.e. no other non-bypassed approvers are still pending).
  // Used to avoid prematurely showing "Approved" when getLineItemApprovalStatus()
  // falls back to 'approved' for users who aren't pending approvers, even though
  // other approvers at this step may still need to act.
  const isCompletedAtCurrentStep = (item: OrderDetailDto['lineItems'][0]) => {
    if (!orderDetail) return true;
    const currentLevelStatuses = item.stepStatuses?.[orderDetail.currentStep];
    if (!currentLevelStatuses || currentLevelStatuses.length === 0) {
      // Fallback for older/partial payloads where stepStatuses may be missing.
      return true;
    }
    const nonBypassedStatuses = currentLevelStatuses.filter(status => status.status !== 'bypassed');
    if (nonBypassedStatuses.length === 0) return true;
    return nonBypassedStatuses.every(status => status.status === 'approved' || status.status === 'accepted');
  };

  // Whether the current user has already recorded a decision (accept/decline/modify) for this
  // line item at the current step, regardless of whether other approvers at the same step are
  // still pending. Used so an item the user already acted on doesn't keep showing action buttons
  // to them just because a co-approver hasn't decided yet.
  const hasCurrentUserDecidedLineItem = (item: OrderDetailDto['lineItems'][0]) => {
    if (!orderDetail || !user) return false;
    const userEmail = user.profile?.email;
    if (!userEmail) return false;
    const currentLevelStatuses = item.stepStatuses?.[orderDetail.currentStep];
    if (!currentLevelStatuses) return false;
    const normalizedUserEmail = normalizeEmail(userEmail);
    return currentLevelStatuses.some(status =>
      normalizeEmail(status.approverEmail) === normalizedUserEmail &&
      status.status?.toLowerCase() !== 'pending'
    );
  };

  // Helper function to check if the current user can act on the order
  const canUserActOnOrder = () => {
    if (!orderDetail || !user || !isAuthenticated) return false;
    
    // Check if order is in progress (not Completed, Cancelled, etc.)
    if (!isOrderInProgress(orderDetail.status)) return false;
    
    // Admins can approve at any level
    if (isUserAdmin()) return true;
    
    // Check if the current user is in the list of pending approvers
    const userEmail = user.profile?.email;
    if (!userEmail) return false;

    // Prefer line-level checks so one declined item does not remove edit access
    // for other items still pending this user at the current step.
    return isCurrentUserInPendingApprovers(userEmail) ||
      orderDetail.lineItems.some(item => canCurrentUserActOnLineItem(item, userEmail));
  };

  // Helper function to calculate adjusted total based on approved quantities
  const calculateAdjustedTotal = () => {
    if (!orderDetail) return 0;

    let adjustedTotal = 0;
    for (const item of orderDetail.lineItems) {
      const draft = orderDetail.currentStepRequireExplicitSubmit && item.id ? draftDecisions[item.id] : undefined;
      if (draft) {
        if (draft.decision === 'decline') continue;
        adjustedTotal += draft.approvedQuantity != null ? item.unitPrice * draft.approvedQuantity : item.totalPrice;
        continue;
      }

      // Skip rejected items
      if (item.approvalStatus?.toLowerCase() === 'rejected' || item.approvalStatus?.toLowerCase() === 'declined') {
        continue;
      }
      
      if (item.isQuantityModified && item.approvedQuantity != null) {
        // Use approved quantity for modified items
        adjustedTotal += item.unitPrice * item.approvedQuantity;
      } else {
        // Use original total for non-modified items
        adjustedTotal += item.totalPrice;
      }
    }
    return adjustedTotal;
  };

  // Helper function to calculate original total (before any modifications)
  const calculateOriginalTotal = () => {
    if (!orderDetail) return 0;
    
    let originalTotal = 0;
    for (const item of orderDetail.lineItems) {
      // Include all items (even rejected ones) at their original totals
      originalTotal += item.totalPrice;
    }
    return originalTotal;
  };

  const submitApprovalMutation = useSubmitApproval();
  const submitBatchApprovalMutation = useSubmitBatchApproval();
  const checkRequiredFieldsMutation = useCheckRequiredFields();
  const upsertReviewDraftMutation = useUpsertOrderReviewDraft();
  const deleteReviewDraftMutation = useDeleteOrderReviewDraft();
  const deleteAllReviewDraftsMutation = useDeleteAllOrderReviewDrafts();
  
  const reprocessOrderMutation = useReprocessOrder();
  const restartWorkflowMutation = useRestartWorkflow();
  const [workflowRetryError, setWorkflowRetryError] = useState<string | null>(null);
  const refreshTemporalStatusMutation = useRefreshTemporalStatus();
  const [temporalRefreshResult, setTemporalRefreshResult] = useState<{
    temporalStatus: string; dbStatus: string; isNotFound: boolean; isInSync: boolean; dbStatusUpdated: boolean;
  } | null>(null);

  // Temporal step detail (admin only)
  const [showTemporalDetail, setShowTemporalDetail] = useState(false);
  const temporalDetailWorkflowId = orderDetail?.temporalWorkflowId ?? '';
  const {
    data: temporalDetail,
    isLoading: isLoadingTemporalDetail,
    refetch: refetchTemporalDetail,
    error: temporalDetailError,
  } = useTemporalWorkflowDetail(
    divisionId || '',
    temporalDetailWorkflowId,
    showTemporalDetail && isOrderAdmin && !!temporalDetailWorkflowId
  );
  
  // Pause/Resume mutations
  const pauseOrderMutation = usePauseOrder();
  const resumeOrderMutation = useResumeOrder();

  // Admin advance (force-advance stuck workflow)
  const adminAdvanceMutation = useAdminAdvanceWorkflow();
  const [showAdminAdvanceModal, setShowAdminAdvanceModal] = useState(false);
  const [adminAdvanceReason, setAdminAdvanceReason] = useState('');
  const [adminAdvanceResult, setAdminAdvanceResult] = useState<{ success: boolean; message: string } | null>(null);

  // Delete order (admin only)
  const deleteOrderMutation = useDeleteOrder();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // State for reprocess notification
  const [reprocessNotification, setReprocessNotification] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: ''
  });

  // Poll for step changes after batch approval
  useEffect(() => {
    if (!isPollingForStepChange || pollingStartStep === null) {
      // Clean up if polling was stopped externally
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // If we already have an interval running, don't start another one
    if (pollingIntervalRef.current) {
      console.log('[Polling] Interval already running, skipping setup');
      return;
    }

    // Reset the stop flag
    shouldStopPollingRef.current = false;
    let pollCount = 0;
    const maxPolls = 15; // 30 seconds at 2 second intervals

    const stopPolling = () => {
      console.log('[Polling] stopPolling called');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      shouldStopPollingRef.current = true;
      setIsPollingForStepChange(false);
      setPollingStartStep(null);
      setPollingStartPendingCount(null);
      setIsProcessingApproval(false);
    };

    const checkAndPoll = async () => {
      // Check if we should stop (might have been stopped externally)
      if (shouldStopPollingRef.current) {
        console.log('[Polling] shouldStopPollingRef is true, aborting check');
        return;
      }

      // Increment poll count before the async operation to avoid race conditions
      pollCount++;
      console.log(`[Polling] Attempt ${pollCount}/${maxPolls}: Started at step ${pollingStartStep}`);

      try {
        // Refetch the query and wait for it to complete
        console.log(`[Polling] Refetching data for order ${orderId}...`);
        await queryClient.refetchQueries({ 
          queryKey: ['orders', 'detail', divisionId, orderId],
          exact: true
        });
        console.log('[Polling] Refetch completed');
        
        // Check again after async operation - we might have been stopped while fetching
        if (shouldStopPollingRef.current) {
          console.log('[Polling] Stopped while fetching - aborting');
          return;
        }
        
        // Read fresh data directly from the cache after refetch completes
        const freshData = queryClient.getQueryData<OrderDetailDto>(['orders', 'detail', divisionId, orderId]);
        const currentStep = freshData?.currentStep;
        
        // Count how many items the current user can still approve
        const currentPendingCount = freshData?.lineItems?.filter(item => 
          getLineItemApprovalStatus(item) === null
        ).length || 0;
        
        console.log(`[Polling] Attempt ${pollCount}: Fresh data - currentStep=${currentStep}, pollingStartStep=${pollingStartStep}, pendingCount=${currentPendingCount}, pollingStartPendingCount=${pollingStartPendingCount}`);
        
        // Check if step has changed OR all pending items have been processed
        const stepChanged = currentStep !== undefined && currentStep !== pollingStartStep;
        const allItemsProcessed = currentPendingCount === 0 && pollingStartPendingCount !== null && pollingStartPendingCount > 0;
        
        if (stepChanged) {
          console.log(`[Polling] ✅ Step changed from ${pollingStartStep} to ${currentStep}`);
          stopPolling();
          return;
        }
        
        if (allItemsProcessed) {
          console.log(`[Polling] ✅ All ${pollingStartPendingCount} pending items have been processed`);
          stopPolling();
          return;
        }
        
        // Check if we've reached max attempts
        if (pollCount >= maxPolls) {
          console.log('[Polling] ⏱️ Timeout reached after 30 seconds');
          stopPolling();
          return;
        }
      } catch (error) {
        console.error('[Polling] Error:', error);
      }
    };

    console.log('[Polling] Setting up interval for step', pollingStartStep);
    
    // Initial check
    checkAndPoll();

    // Set up interval for subsequent checks
    pollingIntervalRef.current = setInterval(checkAndPoll, 2000);

    return () => {
      console.log('[Polling] Cleanup - clearing interval');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isPollingForStepChange, pollingStartStep, pollingStartPendingCount, divisionId, orderId, queryClient]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Helper function to get the effective approval status for a line item
  const getLineItemApprovalStatus = (item: OrderDetailDto['lineItems'][0]) => {
    // Check local state first (for immediate feedback after user action)
    const localStatus = lineItemStatuses[item.id || ''];
    if (localStatus) {
      return localStatus;
    }
    
    // Admins can approve at any level - show approval buttons for all non-rejected items
    if (isUserAdmin()) {
      if (item.approvalStatus === 'rejected' || item.approvalStatus === 'declined') {
        // Don't allow approving rejected items
        return 'rejected';
      }
      // Return null to show approval buttons
      return null;
    }
    
    // CRITICAL FIX: If the current user is assigned as an approver for this line item
    // AT THE CURRENT STEP, they should see approval buttons regardless of the line item's
    // overall approval status. This allows multi-level approvals where each approver needs
    // to approve. We deliberately validate against the CURRENT step's approver list
    // (canCurrentUserActOnLineItem) rather than the order-wide pendingApprovers list, so a
    // user who is assigned to a later/earlier step (but not the current one) doesn't get
    // action buttons for a step they aren't actually assigned to.
    const userEmail = user?.profile?.email;
    const canActOnThisItem = canCurrentUserActOnLineItem(item, userEmail);
    
    if (canActOnThisItem) {
      // User is a pending approver at the current step - show approval buttons for all non-rejected items
      if (item.approvalStatus === 'rejected' || item.approvalStatus === 'declined') {
        // Don't allow approving rejected items
        return 'rejected';
      }
      // Return null to show approval buttons
      return null;
    }
    
    // User is not a pending approver - they should NOT see action buttons
    // Show a status to indicate the current state without allowing actions
    if (item.approvalStatus) {
      const status = item.approvalStatus.toLowerCase();
      
      if (status === 'approved' || status === 'accepted') {
        return 'approved';
      }
      if (status === 'rejected' || status === 'declined') {
        return 'rejected';
      }
      if (status === 'partially_approved' || status === 'pending') {
        // Item is in progress - show as "approved" to prevent action buttons
        // This indicates the user completed their part but others need to approve
        return 'approved';
      }
    }
    
    // Safety: if no status, still don't show buttons to non-pending approvers
    return 'approved';
  };

  const handleApprovalAction = async (
    action: 'accept' | 'decline' | 'modify', 
    lineItemId?: string, 
    lineItemLevel?: number, 
    comments?: string,
    approvedQuantity?: number,
    modificationReason?: string,
    signatureData?: string
  ) => {
    if (!orderDetail || !user || !divisionId) return;

    // For rejections without a reason, open the modal instead
    if (action === 'decline' && !comments) {
      if (lineItemId) {
        // Single item decline
        const lineItem = orderDetail.lineItems.find(item => item.id === lineItemId);
        setRejectionModal({
          isOpen: true,
          lineItemId,
          lineItemName: lineItem?.name,
          lineItemLevel,
          isBulkDecline: false
        });
      } else {
        // Bulk decline (all items)
        setRejectionModal({
          isOpen: true,
          isBulkDecline: true
        });
      }
      return;
    }

    // Draft-review steps (RequireExplicitSubmit): stage the decision locally instead of
    // committing it to the workflow engine immediately. Submit Review sends everything at once.
    if (orderDetail.currentStepRequireExplicitSubmit && lineItemId) {
      if (reviewSnapshotStepRef.current === null) reviewSnapshotStepRef.current = orderDetail.currentStep;
      setDraftDecisions(prev => ({
        ...prev,
        [lineItemId]: {
          decision: action === 'decline' ? 'decline' : 'accept',
          comments,
          approvedQuantity,
          modificationReason,
          signatureData
        }
      }));
      if (divisionId && orderId) {
        upsertReviewDraftMutation.mutate({
          divisionId,
          orderId,
          lineItemId,
          draft: {
            step: orderDetail.currentStep,
            decision: action === 'decline' ? 'decline' : 'accept',
            comments,
            approvedQuantity,
            modificationReason,
            signatureData
          }
        });
      }
      if (action === 'modify') {
        setModificationModal({ isOpen: false });
      } else if (action === 'decline') {
        setRejectionModal({ isOpen: false });
      }
      return;
    }

    // Set processing state to keep spinner visible during entire operation
    setIsProcessingApproval(true);

    try {
      const temporalWorkflowId = `OrderApprovalWorkflow-${orderDetail.id}`;

      // For line item approvals, use the line item's current step
      // For order-level approvals, use the order's current step
      const approvalLevel = lineItemLevel ?? orderDetail.currentStep;

      const approvalRequest: ApprovalDecisionRequest = {
        userId: user.profile?.email || '',
        level: approvalLevel,
        decision: action === 'modify' ? 'accept' : action, // Modify is treated as approve with quantity change
        comments: comments,
        lineItemId: lineItemId,
        approvedQuantity: approvedQuantity,
        modificationReason: modificationReason,
        signatureData: signatureData
      };

      console.log('Submitting approval decision:', {
        action,
        lineItemId,
        approvedQuantity,
        approvalRequest,
        temporalWorkflowId
      });

      // Update local state for immediate feedback
      if (lineItemId) {
        setLineItemStatuses(prev => ({
          ...prev,
          [lineItemId]: action === 'decline' ? 'rejected' : 'approved'
        }));
      }

      const result = await submitApprovalMutation.mutateAsync({
        divisionId: divisionId,
        temporalWorkflowId,
        decision: approvalRequest
      });

      console.log('Approval decision submitted successfully:', result);

      // Add a small delay before refetching to allow backend workflow to complete
      console.log('Waiting for workflow to process...');
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Refetch data to update the UI
      console.log('Refetching order detail...');
      const refetchResult = await refetch();
      console.log('Order detail refetched:', refetchResult.data);
      
      // Close any open modals after successful completion
      // This ensures the modal stays open (with spinner) during the entire process
      if (action === 'modify') {
        console.log('Closing modification modal after successful completion');
        setModificationModal({ isOpen: false });
      } else if (action === 'decline') {
        console.log('Closing rejection modal after successful completion');
        setRejectionModal({ isOpen: false });
      }
    } catch (error: any) {
      console.error('Error submitting approval:', error);
      console.log('Error response:', error?.response);
      console.log('Error response data:', error?.response?.data);
      console.log('Error code:', error?.response?.data?.errorCode);
      
      // Check if this is an admin attempting to approve
      if (error?.response?.data?.errorCode === 'ADMIN_CANNOT_APPROVE') {
        const message = error.response.data.message || 'Administrators cannot approve orders. Please use a non-admin approver account.';
        
        console.log('Admin approval attempt blocked:', message);
        
        // Revert local state for the line item
        if (lineItemId) {
          setLineItemStatuses(prev => ({
            ...prev,
            [lineItemId]: null
          }));
        }
        
        // Show alert to the user
        alert(message);
        
        // Close modals and clear processing state
        setModificationModal({ isOpen: false });
        setRejectionModal({ isOpen: false });
        setIsProcessingApproval(false);
        return; // Exit early
      }
      
      // Check if this is a missing signature validation error
      if (error?.response?.data?.errorCode === 'SIGNATURE_REQUIRED') {
        const message = error.response.data.message || 'A signature is required to confirm pickup at this step.';
        
        // Revert local state for the line item
        if (lineItemId) {
          setLineItemStatuses(prev => ({
            ...prev,
            [lineItemId]: null
          }));
        }
        
        alert(message);
        setIsProcessingApproval(false);
        setIsSubmittingPickupSignature(false);
        return; // Exit early
      }
      
      // Check if this is a quantity-increase validation error (step only allows decreasing quantity)
      if (error?.response?.data?.errorCode === 'QUANTITY_INCREASE_NOT_ALLOWED') {
        const message = error.response.data.message || 'Increasing the quantity is not allowed at this approval step.';
        
        alert(message);
        setIsProcessingApproval(false);
        return; // Exit early - keep the modification modal open so the user can correct the quantity
      }
      
      // Check if this is a required fields validation error
      if (error?.response?.data?.errorCode === 'REQUIRED_FIELDS_MISSING') {
        const missingFields = error.response.data.missingFields || [];
        const message = error.response.data.message || 'Required fields must be completed before approval.';
        
        console.log('Required fields validation error detected!');
        console.log('Missing fields:', missingFields);
        console.log('Message:', message);
        
        // Revert local state since approval was rejected
        if (lineItemId) {
          setLineItemStatuses(prev => ({
            ...prev,
            [lineItemId]: null
          }));
        }
        
        // Show required fields modal instead of generic error
        setRequiredFieldsModal({
          isOpen: true,
          missingFields,
          message
        });
        
        console.log('Required fields modal state set to:', {
          isOpen: true,
          missingFields,
          message
        });
        
        // Don't close the modals - let user see what needs to be done
        setIsProcessingApproval(false);
        return; // Exit early - don't run the generic error handling
      }
      
      // Revert local state on error
      if (lineItemId) {
        setLineItemStatuses(prev => ({
          ...prev,
          [lineItemId]: null
        }));
      }
      
      // Close modals on error as well
      setModificationModal({ isOpen: false });
      setRejectionModal({ isOpen: false });
    } finally {
      // Always clear processing state when done
      setIsProcessingApproval(false);
    }
  };

  const handleBatchApproval = async (action: 'accept' | 'decline', comments?: string, signatureData?: string) => {
    if (!orderDetail || !user || !divisionId) return;

    // For bulk decline, open modal to collect reason
    if (action === 'decline') {
      setRejectionModal({
        isOpen: true,
        isBulkDecline: true
      });
      return;
    }

    // Batch approve all pending items
    // Use getLineItemApprovalStatus to only include items the user can act on, and exclude
    // items already staged as a draft decision (so "Approve Remaining" only targets undecided items).
    const pendingItems = orderDetail.lineItems.filter(item => {
      const effectiveStatus = getLineItemApprovalStatus(item);
      if (effectiveStatus !== null) return false;
      if (item.id && draftDecisions[item.id]) return false;
      return true;
    });

    if (pendingItems.length === 0) return;

    // Draft-review steps: stage all remaining pending items locally instead of submitting now.
    if (orderDetail.currentStepRequireExplicitSubmit) {
      if (reviewSnapshotStepRef.current === null) reviewSnapshotStepRef.current = orderDetail.currentStep;
      setDraftDecisions(prev => {
        const next = { ...prev };
        pendingItems.forEach(item => {
          if (item.id) {
            next[item.id] = { decision: 'accept', comments, signatureData };
          }
        });
        return next;
      });
      if (divisionId && orderId) {
        pendingItems.forEach(item => {
          if (!item.id) return;
          upsertReviewDraftMutation.mutate({
            divisionId,
            orderId,
            lineItemId: item.id,
            draft: { step: orderDetail.currentStep, decision: 'accept', comments, signatureData }
          });
        });
      }
      return;
    }

    setIsProcessingApproval(true);

    try {
      const temporalWorkflowId = `OrderApprovalWorkflow-${orderDetail.id}`;
      
      // Create batch of decisions for all pending items
      const decisions: ApprovalDecisionRequest[] = pendingItems.map(item => ({
        userId: user.profile?.email || '',
        level: orderDetail.currentStep,
        decision: action,
        lineItemId: item.id,
        comments: comments,
        signatureData: signatureData
      }));

      console.log('Submitting batch approval for', decisions.length, 'items');

      // Update local state for immediate feedback
      pendingItems.forEach(item => {
        if (item.id) {
          setLineItemStatuses(prev => ({
            ...prev,
            [item.id!]: 'approved'
          }));
        }
      });

      // Submit batch request
      await submitBatchApprovalMutation.mutateAsync({
        divisionId,
        temporalWorkflowId,
        decisions
      });

      console.log('Batch approval submitted successfully');

      // Store the current step and pending count, then start polling for changes
      setPollingStartStep(orderDetail.currentStep);
      setPollingStartPendingCount(pendingItems.length);
      setIsPollingForStepChange(true);

      // Add delay before initial refetch
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Refetch data to update the UI
      await refetch();
      
      // Note: isProcessingApproval will be cleared by the polling effect when step changes or timeout
    } catch (error: any) {
      console.error('Error submitting batch approval:', error);
      
      // Check for admin error
      if (error?.response?.data?.errorCode === 'ADMIN_CANNOT_APPROVE') {
        const message = error.response.data.message || 'Administrators cannot approve orders. Please use a non-admin approver account.';
        alert(message);
      } 
      // Check for missing signature error
      else if (error?.response?.data?.errorCode === 'SIGNATURE_REQUIRED') {
        const message = error.response.data.message || 'A signature is required to confirm pickup at this step.';
        alert(message);
      }
      // Check for required fields error
      else if (error?.response?.data?.errorCode === 'REQUIRED_FIELDS_MISSING') {
        const missingFields = error.response.data.missingFields || [];
        const message = error.response.data.message || 'Required fields must be completed before approval.';
        
        setRequiredFieldsModal({
          isOpen: true,
          missingFields,
          message
        });
      }
      
      // Revert local state on error
      pendingItems.forEach(item => {
        if (item.id) {
          setLineItemStatuses(prev => ({
            ...prev,
            [item.id!]: null
          }));
        }
      });
      
      // Stop polling on error
      setIsPollingForStepChange(false);
      setPollingStartStep(null);
      setIsProcessingApproval(false);
      setIsSubmittingPickupSignature(false);
    }
  };

  // Opens the pickup signature modal for a Confirm Pickup step, but first checks whether the
  // order has any required fields missing at the current step. If so, the required fields
  // modal is shown instead so the approver fills those in before being prompted to sign.
  const openPickupSignatureFlow = async (payload: { isBulk?: boolean; lineItemId?: string; approvedQuantity?: number; modificationReason?: string }) => {
    if (!orderDetail) return;

    const temporalWorkflowId = `OrderApprovalWorkflow-${orderDetail.id}`;

    try {
      const { missingFields } = await checkRequiredFieldsMutation.mutateAsync({
        divisionId,
        temporalWorkflowId
      });

      if (missingFields && missingFields.length > 0) {
        setRequiredFieldsModal({
          isOpen: true,
          missingFields,
          message: `The following required fields must be completed before approval: ${missingFields.join(', ')}`
        });
        return;
      }
    } catch (error) {
      console.error('Error checking required fields before pickup signature:', error);
      // Fall through and let the normal submit-time validation catch it if this pre-check fails
    }

    // Already signed for this step (e.g. accepted one item, now accepting/modifying another) -
    // reuse that signature instead of prompting again.
    if (capturedSignature && capturedSignature.step === orderDetail.currentStep) {
      setIsSubmittingPickupSignature(true);
      try {
        if (payload.isBulk) {
          await handleBatchApproval('accept', undefined, capturedSignature.signatureData);
        } else if (payload.lineItemId) {
          await handleApprovalAction(
            'accept',
            payload.lineItemId,
            orderDetail.currentStep,
            payload.modificationReason || undefined,
            payload.approvedQuantity,
            payload.modificationReason,
            capturedSignature.signatureData
          );
        }
      } finally {
        setIsSubmittingPickupSignature(false);
      }
      return;
    }

    setPickupSignatureModal({ isOpen: true, ...payload });
  };

  const handlePickupSignatureConfirm = async (signatureData: string, notes: string) => {
    setIsSubmittingPickupSignature(true);
    try {
      if (pickupSignatureModal.isBulk) {
        await handleBatchApproval('accept', notes || undefined, signatureData);
      } else if (pickupSignatureModal.lineItemId) {
        await handleApprovalAction(
          'accept',
          pickupSignatureModal.lineItemId,
          orderDetail?.currentStep,
          notes || pickupSignatureModal.modificationReason || undefined,
          pickupSignatureModal.approvedQuantity,
          pickupSignatureModal.modificationReason,
          signatureData
        );
      }
      if (orderDetail) {
        setCapturedSignature({ step: orderDetail.currentStep, signatureData });
      }
      setPickupSignatureModal({ isOpen: false });
    } finally {
      setIsSubmittingPickupSignature(false);
    }
  };

  const handleRejectionSubmit = async (reason: string) => {
    if (rejectionModal.isBulkDecline) {
      // Decline all pending items with batch request
      // Use getLineItemApprovalStatus to only include items the user can act on, excluding
      // items already staged as a draft decision.
      const pendingItems = orderDetail?.lineItems.filter(item => {
        const effectiveStatus = getLineItemApprovalStatus(item);
        if (effectiveStatus !== null) return false;
        if (item.id && draftDecisions[item.id]) return false;
        return true;
      }) || [];
      
      if (!orderDetail || !user || !divisionId || pendingItems.length === 0) {
        setRejectionModal({ isOpen: false });
        return;
      }

      // Draft-review steps: stage all remaining pending items as declines locally.
      if (orderDetail.currentStepRequireExplicitSubmit) {
        if (reviewSnapshotStepRef.current === null) reviewSnapshotStepRef.current = orderDetail.currentStep;
        setDraftDecisions(prev => {
          const next = { ...prev };
          pendingItems.forEach(item => {
            if (item.id) {
              next[item.id] = { decision: 'decline', comments: reason };
            }
          });
          return next;
        });
        if (orderId) {
          pendingItems.forEach(item => {
            if (!item.id) return;
            upsertReviewDraftMutation.mutate({
              divisionId,
              orderId,
              lineItemId: item.id,
              draft: { step: orderDetail.currentStep, decision: 'decline', comments: reason }
            });
          });
        }
        setRejectionModal({ isOpen: false });
        return;
      }

      setIsProcessingApproval(true);

      try {
        const temporalWorkflowId = `OrderApprovalWorkflow-${orderDetail.id}`;
        
        // Create batch of decisions for all pending items
        const decisions: ApprovalDecisionRequest[] = pendingItems.map(item => ({
          userId: user.profile?.email || '',
          level: orderDetail.currentStep,
          decision: 'decline',
          comments: reason,
          lineItemId: item.id
        }));

        console.log('Submitting batch decline for', decisions.length, 'items');

        // Update local state for immediate feedback
        pendingItems.forEach(item => {
          if (item.id) {
            setLineItemStatuses(prev => ({
              ...prev,
              [item.id!]: 'rejected'
            }));
          }
        });

        // Submit batch request
        await submitBatchApprovalMutation.mutateAsync({
          divisionId,
          temporalWorkflowId,
          decisions
        });

        console.log('Batch decline submitted successfully');

        // Add delay before refetch
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Refetch data to update the UI
        await refetch();
        
        setRejectionModal({ isOpen: false });
      } catch (error: any) {
        console.error('Error submitting batch decline:', error);
        
        // Revert local state on error
        pendingItems.forEach(item => {
          if (item.id) {
            setLineItemStatuses(prev => ({
              ...prev,
              [item.id!]: null
            }));
          }
        });
        
        // Check for required fields validation error
        if (error?.response?.data?.errorCode === 'REQUIRED_FIELDS_MISSING') {
          const missingFields = error.response.data.missingFields || [];
          const message = error.response.data.message || 'Required fields must be completed before this decision.';
          
          setRequiredFieldsModal({
            isOpen: true,
            missingFields,
            message
          });
        }
        
        setRejectionModal({ isOpen: false });
      } finally {
        setIsProcessingApproval(false);
      }
    } else {
      // Single item decline
      if (!rejectionModal.lineItemId) return;
      
      // Call the approval action with the rejection reason
      // Note: Modal will be closed automatically by handleApprovalAction after completion
      await handleApprovalAction(
        'decline', 
        rejectionModal.lineItemId, 
        rejectionModal.lineItemLevel,
        reason
      );
    }
  };

  const handleRejectionCancel = () => {
    setRejectionModal({ isOpen: false });
  };

  const handleModificationSubmit = async (approvedQuantity: number, reason: string) => {
    console.log('handleModificationSubmit called', {
      lineItemId: modificationModal.lineItemId,
      approvedQuantity,
      reason,
      lineItemLevel: modificationModal.lineItemLevel
    });

    if (!modificationModal.lineItemId) {
      console.error('handleModificationSubmit: No lineItemId in modificationModal');
      return;
    }

    // Steps that require a pickup signature can't submit a decision (accept/modify are both
    // submitted as 'accept' under the hood) without one, so route the pending quantity change
    // through the signature flow instead of submitting it directly from this dialog.
    if (orderDetail?.currentStepRequireSignature) {
      const lineItemId = modificationModal.lineItemId;
      setModificationModal({ isOpen: false });
      await openPickupSignatureFlow({
        isBulk: false,
        lineItemId,
        approvedQuantity,
        modificationReason: reason
      });
      return;
    }
    
    // Call the approval action with quantity modification
    // Note: Modal will be closed automatically by handleApprovalAction after completion
    await handleApprovalAction(
      'modify',
      modificationModal.lineItemId,
      modificationModal.lineItemLevel,
      reason, // comments
      approvedQuantity,
      reason // modificationReason (same as comments for now)
    );
  };

  const handleReprocessOrder = async () => {
    if (!divisionId || !orderId || !orderDetail) return;
    
    try {
      console.log('Reprocessing order:', { divisionId, orderId });
      
      const result = await reprocessOrderMutation.mutateAsync({
        divisionId,
        orderId
      });
      
      console.log('Reprocess result:', result);
      
      // Show success notification
      setReprocessNotification({
        show: true,
        type: 'success',
        message: result.workflowId 
          ? `Order matched to workflow: ${result.workflowName || 'Unknown'}` 
          : 'Order reprocessed, but no matching workflow found'
      });
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setReprocessNotification(prev => ({ ...prev, show: false }));
      }, 5000);
      
      // Refetch order detail to show updated workflow info
      await refetch();
      
      // Poll the order a few times to allow the workflow execution to complete
      // This gives Temporal time to create WorkflowExecutionApprovals and LineItemApprovals
      const pollIntervals = [1000, 2000, 3000, 5000]; // Poll at 1s, 2s, 3s, and 5s after initial refetch
      
      for (const delay of pollIntervals) {
        await new Promise(resolve => setTimeout(resolve, delay));
        console.log(`Polling order after ${delay}ms...`);
        await refetch();
      }
      
    } catch (error: any) {
      console.error('Error reprocessing order:', error);
      
      setReprocessNotification({
        show: true,
        type: 'error',
        message: error?.response?.data?.message || 'Failed to reprocess order'
      });
      
      // Auto-hide error notification after 7 seconds
      setTimeout(() => {
        setReprocessNotification(prev => ({ ...prev, show: false }));
      }, 7000);
    }
  };

  const handlePauseOrder = () => {
    // Show modal to collect pause reason
    setPauseReasonModal({ isOpen: true });
  };

  const handlePauseOrderWithReason = async (reason: string) => {
    if (!divisionId || !orderId) return;
    
    try {
      await pauseOrderMutation.mutateAsync({ divisionId, orderId, reason });
      
      // Close modal
      setPauseReasonModal({ isOpen: false });
      setReprocessNotification({
        show: true,
        type: 'success',
        message: `Workflow paused successfully. Reason: ${reason}`
      });
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setReprocessNotification(prev => ({ ...prev, show: false }));
      }, 5000);
      
      // Refetch order details
      setTimeout(() => {
        refetch();
      }, 1000);
    } catch (error: any) {
      console.error('Error pausing order:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to pause workflow';
      setReprocessNotification({
        show: true,
        type: 'error',
        message: errorMessage
      });
      
      // Auto-hide error after 8 seconds
      setTimeout(() => {
        setReprocessNotification(prev => ({ ...prev, show: false }));
      }, 8000);
    }
  };

  const handleResumeOrder = async () => {
    if (!divisionId || !orderId) return;
    
    try {
      await resumeOrderMutation.mutateAsync({ divisionId, orderId });
      setReprocessNotification({
        show: true,
        type: 'success',
        message: 'Workflow resumed successfully. The order will continue processing.'
      });
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setReprocessNotification(prev => ({ ...prev, show: false }));
      }, 5000);
      
      // Refetch order details
      setTimeout(() => {
        refetch();
      }, 1000);
    } catch (error: any) {
      console.error('Error resuming order:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to resume workflow';
      setReprocessNotification({
        show: true,
        type: 'error',
        message: errorMessage
      });
      
      // Auto-hide error after 8 seconds
      setTimeout(() => {
        setReprocessNotification(prev => ({ ...prev, show: false }));
      }, 8000);
    }
  };

  const handleModifyClick = (lineItemId?: string, lineItemLevel?: number) => {
    if (!orderDetail || !lineItemId) return;
    
    const lineItem = orderDetail.lineItems.find(item => item.id === lineItemId);
    if (!lineItem) return;

    // Use approved quantity if it exists (was previously modified), otherwise use original requested quantity
    const defaultQuantity = lineItem.approvedQuantity != null 
      ? lineItem.approvedQuantity 
      : lineItem.quantity;

    setModificationModal({
      isOpen: true,
      lineItemId,
      lineItemName: lineItem.name,
      lineItemLevel,
      originalRequestedQuantity: lineItem.quantity, // Always show the original request
      currentApprovedQuantity: defaultQuantity !== lineItem.quantity ? defaultQuantity : undefined,
      unitOfMeasure: lineItem.unitOfMeasure || 'units'
    });
  };

  const handleModificationCancel = () => {
    setModificationModal({ isOpen: false });
  };

  // Clears a single drafted line decision so the reviewer can reconsider that item.
  const handleUndoDraftDecision = (lineItemId: string) => {
    setDraftDecisions(prev => {
      const next = { ...prev };
      delete next[lineItemId];
      return next;
    });
    if (divisionId && orderId) {
      deleteReviewDraftMutation.mutate({ divisionId, orderId, lineItemId });
    }
  };

  const handleDiscardDrafts = () => {
    const count = Object.keys(draftDecisions).length;
    if (count === 0) return;
    const quantityChanges = Object.values(draftDecisions).filter(d => d.approvedQuantity != null).length;
    const detail = quantityChanges > 0 ? ` and ${quantityChanges} quantity change${quantityChanges !== 1 ? 's' : ''}` : '';
    if (!window.confirm(`This will clear ${count} decision${count !== 1 ? 's' : ''}${detail}. Discard changes?`)) {
      return;
    }
    setDraftDecisions({});
    reviewSnapshotStepRef.current = null;
    if (divisionId && orderId) {
      deleteAllReviewDraftsMutation.mutate({ divisionId, orderId });
    }
  };

  // Sends every drafted line decision to the workflow engine in a single batch request -
  // nothing reaches the workflow until this is explicitly called.
  const handleSubmitReview = async () => {
    if (!orderDetail || !user || !divisionId) return;

    const entries = Object.entries(draftDecisions);
    if (entries.length === 0) return;

    setIsProcessingApproval(true);

    try {
      // Concurrency guard: re-check the order hasn't moved past the step these decisions were
      // drafted against (e.g. another approver/admin action already advanced it) before sending.
      const freshResult = await refetch();
      const freshStep = freshResult.data?.currentStep;
      if (reviewSnapshotStepRef.current !== null && freshStep !== undefined && freshStep !== reviewSnapshotStepRef.current) {
        alert('This order has changed since you started reviewing it. Please review the current state and try again.');
        setDraftDecisions({});
        reviewSnapshotStepRef.current = null;
        if (orderId) {
          deleteAllReviewDraftsMutation.mutate({ divisionId, orderId });
        }
        setIsProcessingApproval(false);
        return;
      }

      const temporalWorkflowId = `OrderApprovalWorkflow-${orderDetail.id}`;

      const decisions: ApprovalDecisionRequest[] = entries.map(([lineItemId, draft]) => ({
        userId: user.profile?.email || '',
        level: orderDetail.currentStep,
        decision: draft.decision,
        lineItemId,
        comments: draft.comments,
        approvedQuantity: draft.approvedQuantity,
        modificationReason: draft.modificationReason,
        signatureData: draft.signatureData
      }));

      await submitBatchApprovalMutation.mutateAsync({
        divisionId,
        temporalWorkflowId,
        decisions
      });

      setDraftDecisions({});
      reviewSnapshotStepRef.current = null;
      if (orderId) {
        deleteAllReviewDraftsMutation.mutate({ divisionId, orderId });
      }
      setPollingStartStep(orderDetail.currentStep);
      setPollingStartPendingCount(entries.length);
      setIsPollingForStepChange(true);

      await new Promise(resolve => setTimeout(resolve, 1500));
      await refetch();
    } catch (error: any) {
      console.error('Error submitting review:', error);

      if (error?.response?.data?.errorCode === 'ADMIN_CANNOT_APPROVE') {
        alert(error.response.data.message || 'Administrators cannot approve orders. Please use a non-admin approver account.');
      } else if (error?.response?.data?.errorCode === 'SIGNATURE_REQUIRED') {
        alert(error.response.data.message || 'A signature is required to confirm pickup at this step.');
      } else if (error?.response?.data?.errorCode === 'REQUIRED_FIELDS_MISSING') {
        const missingFields = error.response.data.missingFields || [];
        setRequiredFieldsModal({
          isOpen: true,
          missingFields,
          message: error.response.data.message || 'Required fields must be completed before approval.'
        });
      } else {
        alert(error?.response?.data?.message || 'Failed to submit review. Please try again.');
      }

      // Keep the drafts so the reviewer doesn't lose their work after a failed submit.
      setIsPollingForStepChange(false);
      setPollingStartStep(null);
      setIsProcessingApproval(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="error">Cancelled</Badge>;
      case 'needsresolved':
      case 'needs resolved':
        return <Badge variant="error">Needs Resolved</Badge>;
      case 'pending':
      case 'processing':
      case 'submitted':
        return <Badge variant="warning">Processing</Badge>;
      case 'partiallycompleted':
      case 'partially completed':
        return <Badge variant="warning">Partially Completed</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getApprovalIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'accepted':
      case 'completed':
        return <i className="fa-solid fa-check text-green-600 dark:text-green-400"></i>;
      case 'declined':
      case 'cancelled':
        return <i className="fa-solid fa-xmark text-red-600 dark:text-red-400"></i>;
      case 'pending':
        return <i className="fa-solid fa-clock text-blue-600 dark:text-blue-400"></i>;
      case 'timeout':
        return <i className="fa-solid fa-hourglass-end text-orange-600 dark:text-orange-400"></i>;
      case 'notified':
        return <i className="fa-solid fa-bell text-blue-600 dark:text-blue-400"></i>;
      case 'bypassed':
        return <i className="fa-solid fa-forward text-gray-500 dark:text-secondary-400"></i>;
      default:
        return <i className="fa-solid fa-minus text-gray-400 dark:text-secondary-500"></i>;
    }
  };

  const extractAddressInfo = (orderDetail: OrderDetailDto) => {
    try {
      // Use the extracted shipping address from the API
      const shippingAddr = orderDetail.shippingAddress;

      return {
        shipTo: {
          name: shippingAddr?.contactName || orderDetail.submitterName || 'Unknown',
          address: shippingAddr?.street1 ? 
            `${shippingAddr.street1}${shippingAddr.street2 ? `, ${shippingAddr.street2}` : ''}` : 
            'Address not available',
          city: shippingAddr?.city || '',
          state: shippingAddr?.state || '',
          zipCode: shippingAddr?.zipCode || '',
          country: shippingAddr?.country || 'United States',
          phone: shippingAddr?.phone || '',
          email: shippingAddr?.email || ''
        },
      };
    } catch (error) {
      console.error('Error extracting address info:', error);
    }

    // Fallback
    return {
      shipTo: {
        name: orderDetail?.submitterName || 'Unknown',
        address: 'Address not available',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States',
        phone: '',
        email: ''
      }
    };
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="Order Details"
            subtitle="Please sign in to view order details"
          />
        </div>
      </SidebarLayout>
    );
  }

  if (isLoading) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="Order Details"
            subtitle="Loading order details..."
          />
          <Card>
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p>Loading order details...</p>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  if (error || !orderDetail) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="Order Details"
            subtitle="Error loading order details"
          />
          <Card>
            <div data-testid="order-error" className="p-6 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">Failed to load order details</p>
              <div className="space-x-2">
                <Button data-testid="try-again-btn" onClick={() => refetch()}>Try Again</Button>
                <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
              </div>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  const addressInfo = extractAddressInfo(orderDetail);

  return (
    <SidebarLayout>
      <div className="py-4 print:hidden">
        {/* Reprocess Notification Toast */}
        {reprocessNotification.show && (
          <div
            role={reprocessNotification.type === 'success' ? 'status' : 'alert'}
            className={`fixed top-20 right-4 z-50 max-w-md rounded-lg shadow-lg p-4 ${
            reprocessNotification.type === 'success' 
              ? 'bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800' 
              : 'bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800'
          }`}>
            <div className="flex items-start gap-3">
              <i className={`fa-solid ${
                reprocessNotification.type === 'success' 
                  ? 'fa-circle-check text-green-600 dark:text-green-400' 
                  : 'fa-circle-exclamation text-red-600 dark:text-red-400'
              } mt-0.5`}></i>
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  reprocessNotification.type === 'success' 
                    ? 'text-green-800' 
                    : 'text-red-800'
                }`}>
                  {reprocessNotification.message}
                </p>
              </div>
              <button
                onClick={() => setReprocessNotification(prev => ({ ...prev, show: false }))}
                className={`${
                  reprocessNotification.type === 'success' 
                    ? 'text-green-600 dark:text-green-400 hover:text-green-700' 
                    : 'text-red-600 dark:text-red-400 hover:text-red-700'
                }`}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Polling Notification */}
        {isPollingForStepChange && (
          <div role="status" className="fixed top-20 right-4 z-50 max-w-md rounded-lg shadow-lg p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <i className="fa-solid fa-spinner fa-spin text-blue-600 dark:text-blue-400 mt-0.5"></i>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  Waiting for workflow to progress to next step...
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  This may take a few seconds
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Order Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-4">
              <Button 
                data-testid="back-btn"
                variant="outline" 
                onClick={() => navigate(-1)}
                className="mr-4"
              >
                <i className="fa-solid fa-arrow-left mr-2"></i>
                Back
              </Button>
              <div>
                <h1 data-testid="order-number" className="text-2xl font-semibold text-gray-800 dark:text-secondary-100">Order {orderDetail.orderNumber}</h1>
                <h2 data-testid="order-submitter" className="text-base text-gray-600 dark:text-secondary-400 mt-1">{orderDetail.submitterName}</h2>
              </div>
            </div>
            <div data-testid="order-status" className="flex items-center gap-2">{getStatusBadge(orderDetail.status)}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Pause/Resume buttons for active workflows */}
            {!isSupport && orderDetail.status.toLowerCase() === 'processing' && (
              <Button 
                data-testid="pause-workflow-btn"
                variant="outline"
                onClick={handlePauseOrder}
                disabled={pauseOrderMutation.isPending}
                title="Pause the workflow for this order"
                className="bg-yellow-50 dark:bg-yellow-950 hover:bg-yellow-100 text-yellow-700 dark:text-yellow-400 border-yellow-300"
              >
                {pauseOrderMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Pausing...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-pause mr-2"></i>
                    Pause Workflow
                  </>
                )}
              </Button>
            )}
            {!isSupport && orderDetail.status.toLowerCase() === 'paused' && (
              <Button 
                data-testid="resume-workflow-btn"
                variant="outline"
                onClick={handleResumeOrder}
                disabled={resumeOrderMutation.isPending}
                title="Resume the paused workflow"
                className="bg-green-50 dark:bg-green-950 hover:bg-green-100 text-green-700 dark:text-green-400 border-green-300"
              >
                {resumeOrderMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Resuming...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-play mr-2"></i>
                    Resume Workflow
                  </>
                )}
              </Button>
            )}
            <Button 
              variant="outline"
              onClick={() => setShowApprovalFlow(true)}
              title="View detailed workflow flow"
            >
              <i className="fa-solid fa-route mr-2"></i>
              Workflow Flow
            </Button>
            {/* Add Approvers button for admins on in-progress orders */}
            {isAdmin && isOrderInProgress(orderDetail.status) && (
              <Button 
                variant="outline"
                onClick={() => setShowAddApprovers(true)}
                title="Add additional approvers to this order"
                className="bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 text-blue-700 border-blue-300"
              >
                <i className="fa-solid fa-user-plus mr-2"></i>
                Add Approvers
              </Button>
            )}
            {/* Restart Level button - re-run current approval level (admin only) */}
            {isAdmin && isOrderInProgress(orderDetail.status) && orderDetail.status?.toLowerCase() !== 'submitted' && (
              <Button
                variant="outline"
                onClick={() => { setAdminAdvanceReason(''); setAdminAdvanceResult(null); setShowAdminAdvanceModal(true); }}
                title="Restart the current approval level: re-sends notification emails and resets approval decisions (admin only)"
                className="bg-orange-50 dark:bg-orange-950 hover:bg-orange-100 text-orange-700 dark:text-orange-400 border-orange-300"
              >
                <i className="fa-solid fa-rotate-left mr-2"></i>
                Restart Level
              </Button>
            )}
            {/* Delete Order button (admin only) */}
            {(roles.includes('Admin') || roles.includes('approvn-admin') || roles.includes('DivisionAdmin')) && (
              <Button
                variant="outline"
                onClick={() => { setDeleteError(null); setShowDeleteModal(true); }}
                title="Permanently delete this order and cancel its workflow (admin only)"
                className="bg-red-50 dark:bg-red-950 hover:bg-red-100 text-red-700 dark:text-red-400 border-red-300"
              >
                <i className="fa-solid fa-trash mr-2"></i>
                Delete Order
              </Button>
            )}
            {/* Show Print Audit Report button for completed orders */}
            {(orderDetail.status.toLowerCase() === 'completed' || 
              orderDetail.status.toLowerCase() === 'approved' ||
              orderDetail.status.toLowerCase() === 'partiallycompleted' ||
              orderDetail.status.toLowerCase() === 'partiallyapproved' ||
              orderDetail.status.toLowerCase() === 'rejected' ||
              orderDetail.status.toLowerCase() === 'declined') && (
              <Button 
                variant="outline"
                onClick={() => setShowAuditReport(true)}
                title="Print order audit report with workflow history"
              >
                <i className="fa-solid fa-print mr-2"></i>
                Audit Report
              </Button>
            )}
            {(() => {
              // Show pick list button when order is in a fulfillment step
              const currentStepType = workflowDetail?.steps?.find(s => s.step === orderDetail.currentStep)?.stepType;
              const isFulfillmentStep = currentStepType?.toLowerCase() === 'fulfillment';
              
              // Debug logging
              if (orderDetail && workflowDetail) {
                console.log('Pick List Button Debug:', {
                  currentStep: orderDetail.currentStep,
                  steps: workflowDetail.steps,
                  currentStepType,
                  currentStepTypeLower: currentStepType?.toLowerCase(),
                  isFulfillmentStep
                });
              }
              
              return isFulfillmentStep && (
                <Button 
                  variant="outline"
                  onClick={() => setShowPickList(true)}
                >
                  <i className="fa-solid fa-list-check mr-2"></i>
                  Pick List
                </Button>
              );
            })()}
            {(isOrderAdmin || isSupport) && (
              <Button 
                variant="outline"
                onClick={() => setShowRawData(true)}
                title="View raw order data"
              >
                <i className="fa-solid fa-code mr-2"></i>
                Raw Data
              </Button>
            )}
          </div>
        </div>

        {/* Pause Reason Alert */}
        {orderDetail.status.toLowerCase() === 'paused' && orderDetail.pauseReason && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-950 border-l-4 border-yellow-400 p-4 rounded-r">
            <div className="flex items-start">
              <i className="fa-solid fa-pause-circle text-yellow-600 mt-0.5 mr-3"></i>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-800 mb-1">Workflow Paused</h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">{orderDetail.pauseReason}</p>
              </div>
            </div>
          </div>
        )}

        {/* Temporal Workflow Start Failure Alert */}
        {orderDetail.workflowStartFailed && (
          <div className="mb-6 bg-red-50 dark:bg-red-950 border-l-4 border-red-500 p-4 rounded-r">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <i className="fa-solid fa-circle-exclamation text-red-500 text-xl mt-0.5"></i>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold text-red-800">Workflow Engine Error</h3>
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  This order was submitted but the approval workflow failed to start in the workflow engine.
                  Approvals cannot proceed until the workflow is started.
                </p>
                {workflowRetryError && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400 font-medium">{workflowRetryError}</p>
                )}
                {isAdmin && (
                  <div className="mt-3">
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                      disabled={restartWorkflowMutation.isPending}
                      onClick={async () => {
                        setWorkflowRetryError(null);
                        try {
                          await restartWorkflowMutation.mutateAsync({ divisionId: divisionId!, orderId: orderId! });
                          queryClient.invalidateQueries({ queryKey: ['order-detail', divisionId, orderId] });
                        } catch (err: any) {
                          setWorkflowRetryError(err?.message || 'Failed to start workflow. Please try again.');
                        }
                      }}
                    >
                      {restartWorkflowMutation.isPending ? (
                        <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Starting Workflow...</>
                      ) : (
                        <><i className="fa-solid fa-rotate-right mr-2"></i>Retry Workflow Start</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Order Summary - Compact Horizontal Layout */}
        <div className="mb-6 pb-4 border-gray-200 dark:border-secondary-700">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {/* Workflow Information */}
            {orderDetail.workflowId ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-secondary-400">Workflow:</span>
                {isOrderAdmin ? (
                  <a
                    href={`/workflows/${divisionId}/${orderDetail.workflowId}/edit`}
                    onClick={(e) => { e.preventDefault(); navigate(`/workflows/${divisionId}/${orderDetail.workflowId}/edit`); }}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 hover:underline flex items-center gap-1"
                    title={`View workflow details: ${workflowDetail?.name || 'Loading...'}`}
                  >
                    <i className="fa-solid fa-diagram-project"></i>
                    <span>{workflowDetail?.name || 'Loading...'}</span>
                  </a>
                ) : (
                  <span className="text-sm font-medium text-gray-700 dark:text-secondary-200 flex items-center gap-1">
                    <i className="fa-solid fa-diagram-project"></i>
                    <span>{workflowDetail?.name || 'Loading...'}</span>
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-secondary-400">Workflow:</span>
                <span className="text-sm text-gray-600 dark:text-secondary-400">No workflow found</span>
                <button
                  onClick={() => navigate(`/simulator?orderId=${orderDetail.id}`)}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 hover:underline flex items-center gap-1 ml-1"
                  title="Open in simulator to find matching workflow"
                  style={{ display: isSupport ? 'none' : undefined }}
                >
                  <i className="fa-solid fa-flask"></i>
                  <span>Open in Simulator</span>
                </button>
                {/* Show reprocess button for orders in Submitted status */}
                {!isSupport && (orderDetail.status?.toLowerCase() === 'submitted' || orderDetail.status?.toLowerCase() === 'unmatched') && (
                  <button
                    onClick={handleReprocessOrder}
                    disabled={reprocessOrderMutation.isPending}
                    className="text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 hover:underline flex items-center gap-1 ml-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Retry workflow matching for this order"
                  >
                    {reprocessOrderMutation.isPending ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-rotate"></i>
                        <span>Reprocess Workflow</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            
            {/* Total Amount */}
            <div data-testid="order-total" className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-secondary-400">Total:</span>
              {(() => {
                const adjustedTotal = calculateAdjustedTotal();
                const originalTotal = calculateOriginalTotal();
                const hasModifications = orderDetail.lineItems.some(item => 
                  item.isQuantityModified || item.approvalStatus?.toLowerCase() === 'rejected' || item.approvalStatus?.toLowerCase() === 'declined' ||
                  Boolean(orderDetail.currentStepRequireExplicitSubmit && item.id && draftDecisions[item.id])
                );
                
                if (hasModifications && adjustedTotal !== originalTotal) {
                  return (
                    <div className="flex items-center gap-2">
                      <span className="line-through text-gray-500 dark:text-secondary-400">
                        {formatCurrency(originalTotal, orderDetail.currency)}
                      </span>
                      <i className="fa-solid fa-arrow-right text-xs text-gray-400 dark:text-secondary-500"></i>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">
                        {formatCurrency(adjustedTotal, orderDetail.currency)}
                      </span>
                    </div>
                  );
                }
                return <span className="font-semibold text-gray-800 dark:text-secondary-100">{formatCurrency(orderDetail.totalAmount, orderDetail.currency)}</span>;
              })()}
            </div>
            
            <span className="text-gray-300 dark:text-secondary-600">|</span>
            
            {/* Created Date */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-secondary-400">Created:</span>
              <span className="font-medium text-gray-800 dark:text-secondary-100">{formatDate(orderDetail.createdAt)}</span>
            </div>
            
            <span className="text-gray-300 dark:text-secondary-600">|</span>
            
            {/* Division */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-secondary-400">Division:</span>
              <span className="font-medium text-gray-800 dark:text-secondary-100">{orderDetail.divisionName}</span>
            </div>
            
            <span className="text-gray-300 dark:text-secondary-600">|</span>
            
            {/* Submitter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-secondary-400">Submitter:</span>
              <span className="font-medium text-gray-800 dark:text-secondary-100">{orderDetail.submitterName}</span>
            </div>
            
            {/* Vendor Info */}
            {orderDetail.vendor && (
              <>
                <span className="text-gray-300 dark:text-secondary-600">|</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-secondary-400">Vendor:</span>
                  <span className="font-medium text-gray-800 dark:text-secondary-100">{orderDetail.vendor.name}</span>
                </div>
                {(orderDetail.vendor.email || orderDetail.vendor.phone) && (
                  <>
                    <span className="text-gray-300 dark:text-secondary-600">|</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-secondary-400">Contact:</span>
                      <span className="font-medium text-gray-800 dark:text-secondary-100">{orderDetail.vendor.email || orderDetail.vendor.phone}</span>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Temporal Engine Row */}
          {orderDetail.temporalWorkflowId && (
            <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 pt-3 border-t border-gray-100 dark:border-secondary-700">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-gears text-gray-400 dark:text-secondary-500 text-sm"></i>
                <span className="text-sm text-gray-500 dark:text-secondary-400">Temporal Engine:</span>
                {(() => {
                  const s = orderDetail.temporalWorkflowStatus ?? '';
                  const map: Record<string, { label: string; cls: string }> = {
                    'temporal_pending':   { label: 'Start Failed',  cls: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400' },
                    'temporal_running':   { label: 'Running',       cls: 'bg-blue-100 dark:bg-blue-900 text-blue-700' },
                    'temporal_approved':  { label: 'Completed',     cls: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400' },
                    'temporal_rejected':  { label: 'Rejected',      cls: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400' },
                    'temporal_timeout':   { label: 'Timed Out',     cls: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400' },
                    'temporal_cancelled': { label: 'Cancelled',     cls: 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400' },
                    'temporal_failed':    { label: 'Failed',        cls: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400' },
                    'temporal_not_found': { label: 'Not in Temporal', cls: 'bg-gray-100 dark:bg-secondary-700 text-gray-500 dark:text-secondary-400' },
                    'temporal_in_progress': { label: 'In Progress', cls: 'bg-blue-100 dark:bg-blue-900 text-blue-700' },
                    'started':            { label: 'Running',       cls: 'bg-blue-100 dark:bg-blue-900 text-blue-700' },
                    'in_progress':       { label: 'In Progress',   cls: 'bg-blue-100 dark:bg-blue-900 text-blue-700' },
                    'completed':         { label: 'Completed',     cls: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400' },
                    'failed':            { label: 'Failed',        cls: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400' },
                    'cancelled':         { label: 'Cancelled',     cls: 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400' },
                  };
                  const entry = map[s] ?? { label: s || 'Unknown', cls: 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400' };
                  return (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${entry.cls}`}>
                      {entry.label}
                    </span>
                  );
                })()}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500 dark:text-secondary-400 font-mono">{orderDetail.temporalWorkflowId}</span>
                <button
                  onClick={() => navigator.clipboard.writeText(orderDetail.temporalWorkflowId!)}
                  className="text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200"
                  title="Copy workflow ID"
                >
                  <i className="fa-regular fa-copy text-xs"></i>
                </button>
              </div>
              {isOrderAdmin && (
                <button
                  onClick={async () => {
                    setTemporalRefreshResult(null);
                    try {
                      const result = await refreshTemporalStatusMutation.mutateAsync({ divisionId: divisionId!, orderId: orderId! });
                      setTemporalRefreshResult(result);
                      if (result.dbStatusUpdated) {
                        queryClient.invalidateQueries({ queryKey: ['order-detail', divisionId, orderId] });
                      }
                    } catch {
                      setTemporalRefreshResult(null);
                    }
                  }}
                  disabled={refreshTemporalStatusMutation.isPending}
                  className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-50"
                  title="Query Temporal engine for live workflow status"
                >
                  {refreshTemporalStatusMutation.isPending
                    ? <><i className="fa-solid fa-spinner fa-spin"></i><span>Checking...</span></>
                    : <><i className="fa-solid fa-arrow-rotate-right"></i><span>Check Temporal</span></>}
                </button>
              )}
              {temporalRefreshResult && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 dark:text-secondary-400">Live:</span>
                  {temporalRefreshResult.isNotFound ? (
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-secondary-700 text-gray-500 dark:text-secondary-400">Not found in Temporal</span>
                  ) : (
                    <span className={`px-1.5 py-0.5 rounded font-medium ${
                      temporalRefreshResult.temporalStatus === 'Running' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700' :
                      temporalRefreshResult.temporalStatus === 'Completed' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400' :
                      temporalRefreshResult.temporalStatus === 'Failed' ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400' :
                      'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400'
                    }`}>{temporalRefreshResult.temporalStatus}</span>
                  )}
                  {!temporalRefreshResult.isInSync && (
                    <span className="text-amber-600 dark:text-amber-400" title={`DB shows: ${temporalRefreshResult.dbStatus}`}>
                      <i className="fa-solid fa-triangle-exclamation"></i> Out of sync
                    </span>
                  )}
                </div>
              )}
              {isOrderAdmin && (
                <button
                  onClick={() => {
                    if (!showTemporalDetail) {
                      setShowTemporalDetail(true);
                    } else {
                      setShowTemporalDetail(false);
                    }
                  }}
                  className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700"
                  title="Show per-step approver detail from Temporal"
                >
                  <i className={`fa-solid ${showTemporalDetail ? 'fa-chevron-up' : 'fa-list-check'}`}></i>
                  <span>{showTemporalDetail ? 'Hide Step Detail' : 'Step Detail'}</span>
                </button>
              )}
              {isOrderAdmin && (
                <button
                  onClick={() => setShowActivityLog(v => !v)}
                  className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700"
                  title="Show full activity log for this order"
                >
                  <i className={`fa-solid ${showActivityLog ? 'fa-chevron-up' : 'fa-timeline'}`}></i>
                  <span>{showActivityLog ? 'Hide Activity Log' : 'Activity Log'}</span>
                </button>
              )}
            </div>

            {/* Step-by-step detail panel */}
            {showTemporalDetail && isOrderAdmin && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-secondary-700">
                {isLoadingTemporalDetail && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-secondary-400 py-2">
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    <span>Loading workflow step detail...</span>
                  </div>
                )}
                {temporalDetailError && (
                  <div className="text-xs text-red-600 dark:text-red-400 py-2">
                    <i className="fa-solid fa-circle-exclamation mr-1"></i>
                    Could not load step detail.
                  </div>
                )}
                {temporalDetail && !isLoadingTemporalDetail && (
                  <div className="space-y-3">
                    {/* Summary line */}
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-secondary-400">
                      <span>Step <strong>{temporalDetail.currentLevel}</strong> of <strong>{temporalDetail.totalLevels}</strong></span>
                      <span className="text-gray-300 dark:text-secondary-600">|</span>
                      <span>Started {new Date(temporalDetail.startedAt).toLocaleString()}</span>
                      {temporalDetail.completedAt && (
                        <><span className="text-gray-300 dark:text-secondary-600">|</span>
                        <span>Completed {new Date(temporalDetail.completedAt).toLocaleString()}</span></>
                      )}
                    </div>

                    {/* Step rows */}
                    {temporalDetail.workflowSteps.map((step) => {
                      const stepIcon: Record<string, string> = {
                        Completed:  'fa-circle-check text-green-500',
                        InProgress: 'fa-circle-half-stroke text-blue-500',
                        Pending:    'fa-circle text-gray-300 dark:text-secondary-600',
                        Declined:   'fa-circle-xmark text-red-500',
                        Timeout:    'fa-clock text-orange-400',
                      };
                      const icon = stepIcon[step.status] ?? 'fa-circle text-gray-300 dark:text-secondary-600';

                      const stepBadge: Record<string, string> = {
                        Completed:  'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400',
                        InProgress: 'bg-blue-100 dark:bg-blue-900 text-blue-700',
                        Pending:    'bg-gray-100 dark:bg-secondary-700 text-gray-500 dark:text-secondary-400',
                        Declined:   'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400',
                        Timeout:    'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400',
                      };
                      const badge = stepBadge[step.status] ?? 'bg-gray-100 dark:bg-secondary-700 text-gray-500 dark:text-secondary-400';

                      const approvedCount = step.approvers.filter(a =>
                        a.status === 'approved' || a.status === 'accepted').length;
                      const totalApprovers = step.approvers.length;

                      return (
                        <div key={step.step} className="pl-1">
                          {/* Step header */}
                          <div className="flex items-center gap-2 mb-1">
                            <i className={`fa-solid ${icon} text-sm`}></i>
                            <span className="text-xs font-medium text-gray-700 dark:text-secondary-200">
                              Step {step.step} — {step.name}
                            </span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badge}`}>
                              {step.status}
                            </span>
                            {totalApprovers > 0 && (
                              <span className="text-xs text-gray-400 dark:text-secondary-500">
                                {approvedCount}/{totalApprovers} approval{totalApprovers !== 1 ? 's' : ''}
                              </span>
                            )}
                            {step.startedAt && step.status === 'InProgress' && step.timeoutAt && (
                              <span className="text-xs text-orange-500" title="Timeout">
                                <i className="fa-regular fa-clock mr-0.5"></i>
                                {(() => {
                                  const msLeft = new Date(step.timeoutAt).getTime() - Date.now();
                                  if (msLeft <= 0) return 'Timed out';
                                  const totalH = Math.floor(msLeft / 3_600_000);
                                  const d = Math.floor(totalH / 24);
                                  const h = totalH % 24;
                                  const m = Math.floor((msLeft % 3_600_000) / 60_000);
                                  return d > 0 ? `${d}d ${h}h ${m}m left` : `${h}h ${m}m left`;
                                })()}
                              </span>
                            )}
                          </div>

                          {/* Approver rows */}
                          {step.approvers.length > 0 && (
                            <div className="ml-5 space-y-1">
                              {step.approvers.map((approver, i) => {
                                const aprStatusCls: Record<string, string> = {
                                  approved:  'text-green-600 dark:text-green-400',
                                  accepted:  'text-green-600 dark:text-green-400',
                                  rejected:  'text-red-500',
                                  declined:  'text-red-500',
                                  timeout:   'text-orange-500',
                                  pending:   'text-gray-400 dark:text-secondary-500',
                                };
                                const aprIcon: Record<string, string> = {
                                  approved:  'fa-check',
                                  accepted:  'fa-check',
                                  rejected:  'fa-xmark',
                                  declined:  'fa-xmark',
                                  timeout:   'fa-clock',
                                  pending:   'fa-hourglass-half',
                                };
                                const cls = aprStatusCls[approver.status] ?? 'text-gray-400 dark:text-secondary-500';
                                const ico = aprIcon[approver.status] ?? 'fa-hourglass-half';

                                return (
                                  <div key={i} className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-secondary-400">
                                    <i className={`fa-solid ${ico} ${cls} mt-0.5 w-3 shrink-0`}></i>
                                    <span className="font-medium">{approver.userName}</span>
                                    {approver.role && approver.role !== 'undefined' && (
                                      <span className="text-gray-400 dark:text-secondary-500">({approver.role})</span>
                                    )}
                                    <span className={`capitalize ${cls}`}>{approver.status}</span>
                                    {approver.respondedAt && (
                                      <span className="text-gray-400 dark:text-secondary-500">
                                        · {new Date(approver.respondedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                    {approver.comments && (
                                      <span className="text-gray-500 dark:text-secondary-400 italic truncate max-w-xs" title={approver.comments}>
                                        — "{approver.comments}"
                                      </span>
                                    )}
                                    {approver.delegatedFromUserName && (
                                      <span className="text-gray-400 dark:text-secondary-500 text-xs">
                                        (delegated from {approver.delegatedFromUserName})
                                      </span>
                                    )}
                                    {approver.timeoutAt && approver.status === 'pending' && (
                                      <span className="text-orange-400" title="Approver timeout">
                                        {(() => {
                                          const msLeft = new Date(approver.timeoutAt).getTime() - Date.now();
                                          if (msLeft <= 0) return 'timed out';
                                          const totalH = Math.floor(msLeft / 3_600_000);
                                          const d = Math.floor(totalH / 24);
                                          const h = totalH % 24;
                                          const m = Math.floor((msLeft % 3_600_000) / 60_000);
                                          return d > 0 ? `· ${d}d ${h}h ${m}m left` : `· ${h}h ${m}m left`;
                                        })()}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Admin activity log panel */}
            {isOrderAdmin && (
              <OrderActivityLog
                divisionId={divisionId!}
                orderId={orderId!}
                visible={showActivityLog}
              />
            )}
            </>
          )}
        </div>

        {/* Order Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {/* Ship To Information */}
          <Card>
            <div 
              className="flex justify-between items-center p-4 cursor-pointer" 
              onClick={() => toggleSection('ship-to')}
            >
              <h2 className="text-base font-semibold">Ship To</h2>
              <div className="flex items-center">
                <i className={`fa-solid fa-chevron-down text-sm transition-transform ${
                  expandedSections['ship-to'] ? 'rotate-180' : ''
                }`}></i>
              </div>
            </div>
            {expandedSections['ship-to'] && (
              <div className="px-4 pb-4">
                <div className="space-y-1.5 text-sm">
                  <p className="font-medium text-gray-900 dark:text-white">{addressInfo.shipTo.name}</p>
                  <p className="text-gray-600 dark:text-secondary-400">{addressInfo.shipTo.address}</p>
                  <p className="text-gray-600 dark:text-secondary-400">
                    {addressInfo.shipTo.city && addressInfo.shipTo.state 
                      ? `${addressInfo.shipTo.city}, ${addressInfo.shipTo.state} ${addressInfo.shipTo.zipCode}`
                      : 'City, State ZIP not available'
                    }
                  </p>
                  <p className="text-gray-600 dark:text-secondary-400">{addressInfo.shipTo.country}</p>
                  {addressInfo.shipTo.phone && (
                    <p className="text-gray-600 dark:text-secondary-400">📞 {addressInfo.shipTo.phone}</p>
                  )}
                  {addressInfo.shipTo.email && (
                    <p className="text-gray-600 dark:text-secondary-400">✉️ {addressInfo.shipTo.email}</p>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* Order Attributes Section */}
          {orderDetail.workflowId && (
            <OrderAttributesSection
              workflowId={orderDetail.workflowId}
              orderId={orderDetail.id}
              step={orderDetail.currentStep}
              orderAttributes={orderDetail.editableFields || []}
              isActiveApprover={canUserActOnOrder()}
              onFieldUpdated={() => {
                // Refetch order details when a field is updated
                refetch();
              }}
            />
          )}

          {/* Notes Section */}
          {(orderDetail.notes || orderDetail.deliveryNotes) && (
            <Card>
              <div 
                className="flex justify-between items-center p-4 cursor-pointer" 
                onClick={() => toggleSection('notes')}
              >
                <h2 className="text-base font-semibold">Notes</h2>
                <i className={`fa-solid fa-chevron-down text-sm transition-transform ${
                  expandedSections['notes'] ? 'rotate-180' : ''
                }`}></i>
              </div>
              {expandedSections['notes'] && (
                <div className="px-4 pb-4">
                  <div className="space-y-3">
                    {orderDetail.notes && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-700 dark:text-secondary-200">Order Notes:</p>
                        <div className="bg-gray-50 dark:bg-secondary-900 rounded p-2">
                          <pre className="whitespace-pre-wrap text-xs text-gray-800 dark:text-secondary-100 font-mono">
                            {orderDetail.notes}
                          </pre>
                        </div>
                      </div>
                    )}
                    {orderDetail.deliveryNotes && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-700 dark:text-secondary-200">Delivery Notes:</p>
                        <div className="bg-blue-50 dark:bg-blue-950 rounded p-2">
                          <pre className="whitespace-pre-wrap text-xs text-gray-800 dark:text-secondary-100 font-mono">
                            {orderDetail.deliveryNotes}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Order Approval History */}
          <Card>
            <div 
              className="flex justify-between items-center p-4 cursor-pointer" 
              onClick={() => toggleSection('order-approval')}
            >
              <h2 className="text-base font-semibold">Order {getCurrentStepType()}</h2>
              <i className={`fa-solid fa-chevron-down text-sm transition-transform ${
                expandedSections['order-approval'] ? 'rotate-180' : ''
              }`}></i>
            </div>
            {expandedSections['order-approval'] && (
              <div className="px-4 pb-4">
                {/* Show special message for NeedsResolved status */}
                {orderDetail.status === 'NeedsResolved' ? (
                  <div className="bg-yellow-50 dark:bg-yellow-950 border-l-4 border-yellow-400 p-4 rounded">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <i className="fa-solid fa-exclamation-triangle text-yellow-400 text-xl"></i>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          No Valid Approvers Found
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                          <p>
                            This order cannot proceed because no users have all the required match identifiers 
                            to approve it. An administrator needs to:
                          </p>
                          <ol className="list-decimal list-inside mt-2 space-y-1">
                            <li>Review the order details and line item requirements</li>
                            <li>Ensure at least one user has all required match identifiers configured</li>
                            <li>Use the "Reprocess" feature to retry approver resolution</li>
                          </ol>
                          {isOrderAdmin && (
                            <div className="mt-3">
                              <Button
                                size="sm"
                                onClick={() => navigate('/needs-resolved')}
                                className="bg-yellow-600 hover:bg-yellow-700"
                              >
                                <i className="fa-solid fa-tools mr-2"></i>
                                Go to Needs Resolved Page
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                <div className="space-y-4">
                  {/* Group approvals by level */}
                  {Array.from({ length: orderDetail.totalSteps }, (_, index) => {
                    const level = index + 1;
                    const levelApprovals = orderDetail.approvalHistory.filter(approval => approval.level === level);
                    const levelName = orderDetail.stepNames?.[level] || `Level ${level}`;
                    
                    // Calculate line item summary statistics for this level based on each item's status at this level
                    let fullyApprovedAtLevel = 0;  // Completed ALL levels
                    let approvedAtLevel = 0;        // Approved at THIS level
                    let rejectedAtLevel = 0;
                    let pendingAtLevel = 0;
                    let timedOutAtLevel = 0;
                    let modifiedAtLevel = 0;
                    // Also treat a "Cancelled" order as timed-out when it was actually cancelled via
                    // TimeoutAction="reject" (order.status ends up "Cancelled", not "Timeout", but the
                    // approval history for the stuck level still has a 'timeout' action) so these steps
                    // render the same "timed out" / "cancelled" copy as a plain-Timeout order instead of
                    // falling through to a misleading "Pending"/"Not Started" state.
                    const isOrderTimedOut = orderDetail.status === 'Timeout' ||
                      (orderDetail.status === 'Cancelled' && orderDetail.approvalHistory.some(a => a.action === 'timeout'));
                    
                    // Check each line item's status at this specific level
                    orderDetail.lineItems.forEach(item => {
                      // First, check if this item was rejected at a previous level
                      // If so, it shouldn't be counted at this level
                      if (level > 1 && item.stepStatuses) {
                        let rejectedAtPreviousLevel = false;
                        for (let prevLevel = 1; prevLevel < level; prevLevel++) {
                          const prevLevelStatuses = item.stepStatuses[prevLevel];
                          if (prevLevelStatuses && prevLevelStatuses.some(s => s.status === 'rejected')) {
                            rejectedAtPreviousLevel = true;
                            break;
                          }
                        }
                        if (rejectedAtPreviousLevel) {
                          // Item was rejected at a previous level, don't count it at this level
                          return;
                        }
                      }
                      
                      if (!item.stepStatuses || !item.stepStatuses[level]) {
                        // No status for this level - check if the item should have reached this level.
                        // Bypassed levels never update highestCompletedStep, so for the current step
                        // we count items as pending unconditionally (they're waiting here regardless
                        // of whether previous steps were bypassed).
                        const itemHighestLevel = item.highestCompletedStep || 0;
                        const isCurrentStep = level === orderDetail.currentStep;
                        const isTerminalOrder = orderDetail.status === 'Cancelled' || orderDetail.status === 'Rejected' || isOrderTimedOut;

                        // If the order timed out waiting on this level, show the level as timed out
                        // rather than silently pending (or omitted for terminal statuses).
                        if (isOrderTimedOut && isCurrentStep) {
                          timedOutAtLevel++;
                        } else if (!isTerminalOrder && level >= orderDetail.currentStep && (isCurrentStep || level === 1 || itemHighestLevel >= (level - 1))) {
                          pendingAtLevel++;
                        }
                        return;
                      }
                      
                      const levelStatuses = item.stepStatuses[level];
                      
                      // Filter out bypassed approvers - they don't count toward completion
                      const nonBypassedStatuses = levelStatuses.filter(s => s.status !== 'bypassed');
                      
                      const hasApproved = nonBypassedStatuses.some(s => s.status === 'approved' || s.status === 'accepted');
                      const hasRejected = nonBypassedStatuses.some(s => s.status === 'rejected');
                      const hasTimedOut = nonBypassedStatuses.some(s => s.status === 'timeout');
                      const allApproved = nonBypassedStatuses.length > 0 && nonBypassedStatuses.every(s => s.status === 'approved' || s.status === 'accepted');
                      // Only count as pending if there are pending statuses AND not all are approved/accepted
                      const isPending = nonBypassedStatuses.some(s => s.status === 'pending') && !allApproved;
                      
                      // Check if this item was modified at this level
                      const isModified = levelStatuses.some(s => 
                        s.approvedQuantity != null && 
                        s.approvedQuantity !== item.quantity
                      );
                      
                      if (isModified) modifiedAtLevel++;
                      
                      if (hasRejected) {
                        rejectedAtLevel++;
                      } else if (allApproved) {
                        // Item is approved at THIS level by all (non-bypassed) approvers
                        const itemHighestLevel = item.highestCompletedStep || 0;
                        
                        // Determine if this item is fully approved or partially approved
                        // For the level we're viewing:
                        // - "Fully Approved" = item completed ALL levels (is done with entire approval process)
                        // - "Partially Approved" = item completed THIS level but has more levels to complete
                        
                        if (itemHighestLevel >= orderDetail.totalSteps) {
                          // Item has completed all levels - fully approved
                          fullyApprovedAtLevel++;
                        } else if (itemHighestLevel >= level) {
                          // Item has completed this level but not all levels - approved at this level
                          approvedAtLevel++;
                        } else {
                          // All approvers at this level approved, but the level isn't marked as complete yet
                          // This can happen if there's a data sync issue
                          // Treat as approved at this level to be safe
                          approvedAtLevel++;
                        }
                      } else if (hasTimedOut && !isPending) {
                        // No pending approvers left (all either timed out or approved-but-not-all), and
                        // at least one approver timed out without acting - show as timed out.
                        timedOutAtLevel++;
                      } else if (isOrderTimedOut && level === orderDetail.currentStep && (isPending || hasTimedOut)) {
                        // The order has since timed out at this exact level. Some pre-existing data may
                        // still show the raw approver status as "pending" (rather than "timeout") if the
                        // record predates the fix that marks LineItemApprovals as timed out - treat any
                        // non-approved item at the stuck level as timed out rather than pending in that case.
                        timedOutAtLevel++;
                      } else if (isPending || hasApproved) {
                        // Has some approvals but not all at this level, or is pending
                        pendingAtLevel++;
                      }
                    });
                    
                    const totalItemsAtLevel = fullyApprovedAtLevel + approvedAtLevel + rejectedAtLevel + pendingAtLevel + timedOutAtLevel;
                    
                    return (
                      <div key={level} className="border-b pb-3 last:border-b-0">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase">{levelName}</h3>
                          {totalItemsAtLevel > 0 && (
                            <div className="flex flex-wrap gap-1.5 text-xs">
                              {(fullyApprovedAtLevel + approvedAtLevel - modifiedAtLevel) > 0 && (
                                <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400 rounded-full">
                                  <i className="fa-solid fa-check"></i> {fullyApprovedAtLevel + approvedAtLevel - modifiedAtLevel}
                                </span>
                              )}
                              {modifiedAtLevel > 0 && (
                                <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 rounded-full">
                                  <i className="fa-solid fa-pen-to-square"></i> {modifiedAtLevel}
                                </span>
                              )}
                              {pendingAtLevel > 0 && (
                                <span className="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400 rounded-full">
                                  <i className="fa-solid fa-clock"></i> {pendingAtLevel}
                                </span>
                              )}
                              {timedOutAtLevel > 0 && (
                                <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400 rounded-full">
                                  <i className="fa-solid fa-hourglass-end"></i> {timedOutAtLevel}
                                </span>
                              )}
                              {rejectedAtLevel > 0 && (
                                <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400 rounded-full">
                                  <i className="fa-solid fa-times"></i> {rejectedAtLevel}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {levelApprovals.length > 0 ? (
                            levelApprovals.map((approval, idx) => {
                              const isTerminalOrder = orderDetail.status === 'Cancelled' || orderDetail.status === 'Rejected';
                              const isPendingOnTerminal = isTerminalOrder && approval.action === 'pending';
                              return (
                              <div key={idx} className="border-l-2 border-gray-200 dark:border-secondary-700 pl-3">
                                <div className="flex items-start space-x-2">
                                  <div className="flex-shrink-0">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                      isPendingOnTerminal ? 'bg-gray-100 dark:bg-secondary-700' :
                                      approval.action === 'accept' ? 'bg-green-100 dark:bg-green-900' : 
                                      approval.action === 'decline' ? 'bg-red-100 dark:bg-red-900' :
                                      approval.action === 'timeout' ? 'bg-orange-100 dark:bg-orange-900' :
                                      approval.action === 'bypassed' ? 'bg-gray-100 dark:bg-secondary-700' : 'bg-blue-100 dark:bg-blue-900'
                                    }`}>
                                      {isPendingOnTerminal
                                        ? <i className="fa-solid fa-minus text-gray-400 dark:text-secondary-500"></i>
                                        : getApprovalIcon(approval.action)}
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{approval.approverName}</p>
                                    {isPendingOnTerminal && (
                                      <p className="text-xs text-gray-400 dark:text-secondary-500 mt-0.5">Not reached</p>
                                    )}
                                    {!isPendingOnTerminal && approval.delegatedFromUserName && (
                                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                        <i className="fa-solid fa-user-group mr-1"></i>
                                        On behalf of {approval.delegatedFromUserName}
                                      </p>
                                    )}
                                    {!isPendingOnTerminal && approval.comments && 
                                     !approval.comments.match(/^(Approved|Rejected|approved|rejected)\s+\d+\s+(item|items)/i) && 
                                     approval.comments !== "Historical approval (no detailed event data available)" && (
                                      <p className="text-xs text-gray-600 dark:text-secondary-400 mt-0.5 line-clamp-2">{approval.comments}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              );
                            })
                          ) : level === orderDetail.currentStep && orderDetail.status !== 'Cancelled' && orderDetail.status !== 'Rejected' && orderDetail.status !== 'Timeout' ? (
                            <div className="flex items-start space-x-2">
                              <div className="flex-shrink-0">
                                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-xs">
                                  <i className="fa-solid fa-clock text-blue-600 dark:text-blue-400"></i>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">Pending {getCurrentStepType()}</p>
                                <p className="text-xs text-gray-500 dark:text-secondary-400">Awaiting {getCurrentStepType().toLowerCase()}ers</p>
                                {orderDetail.pendingApprovers.length > 0 && (
                                  <p className="text-xs text-gray-600 dark:text-secondary-400 mt-0.5 truncate">
                                    {orderDetail.pendingApprovers.join(', ')}
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : isOrderTimedOut && level === orderDetail.currentStep ? (
                            <div className="flex items-start space-x-2">
                              <div className="flex-shrink-0">
                                <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center text-xs">
                                  <i className="fa-solid fa-hourglass-end text-orange-600 dark:text-orange-400"></i>
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">Timed Out</p>
                                <p className="text-xs text-gray-500 dark:text-secondary-400">No response before the timeout deadline</p>
                              </div>
                            </div>
                          ) : isOrderTimedOut && level > orderDetail.currentStep ? (
                            <div className="flex items-start space-x-2">
                              <div className="flex-shrink-0">
                                <div className="w-6 h-6 bg-gray-200 dark:bg-secondary-700 rounded-full flex items-center justify-center text-xs">
                                  <i className="fa-solid fa-ban text-gray-500 dark:text-secondary-400"></i>
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">Cancelled</p>
                                <p className="text-xs text-gray-500 dark:text-secondary-400">Order timed out - this step was never reached</p>
                              </div>
                            </div>
                          ) : totalItemsAtLevel > 0 || level < orderDetail.currentStep ? (
                            <div className="flex items-start space-x-2">
                              <div className="flex-shrink-0">
                                <div className="w-6 h-6 bg-gray-100 dark:bg-secondary-700 rounded-full flex items-center justify-center text-xs">
                                  <i className="fa-solid fa-check text-gray-600 dark:text-secondary-400"></i>
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">Level Completed</p>
                                <p className="text-xs text-gray-500 dark:text-secondary-400">
                                  {levelApprovals.length === 0 && totalItemsAtLevel === 0
                                    ? 'No approver assigned'
                                    : 'Approvals processed'}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start space-x-2">
                              <div className="flex-shrink-0">
                                <div className="w-6 h-6 bg-gray-100 dark:bg-secondary-700 rounded-full flex items-center justify-center text-xs">
                                  <i className="fa-solid fa-minus text-gray-400 dark:text-secondary-500"></i>
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">Not Started</p>
                                <p className="text-xs text-gray-500 dark:text-secondary-400">Awaiting previous level</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Order Items */}
        <Card>
          <div className="flex justify-between items-center p-6 mb-4">
            <h2 className="text-lg font-semibold">Order Items</h2>
            {canUserActOnOrder() && (() => {
              // Calculate item counts for dynamic button labels
              // Use getLineItemApprovalStatus to count items the user can actually act on, and
              // treat already-drafted items (RequireExplicitSubmit steps) as decided too so
              // "Remaining" counts/labels reflect what's actually left to review.
              const pendingItems = orderDetail.lineItems.filter(item => {
                const effectiveStatus = getLineItemApprovalStatus(item);
                if (effectiveStatus !== null) return false;
                if (item.id && draftDecisions[item.id]) return false;
                return true;
              }).length;

              const rejectedItems = orderDetail.lineItems.filter(item => {
                const effectiveStatus = getLineItemApprovalStatus(item);
                if (effectiveStatus === 'rejected') return true;
                return Boolean(item.id && draftDecisions[item.id]?.decision === 'decline');
              }).length;

              const approvedItems = orderDetail.lineItems.filter(item => {
                const effectiveStatus = getLineItemApprovalStatus(item);
                if (effectiveStatus === 'approved') return true;
                return Boolean(item.id && draftDecisions[item.id]?.decision === 'accept');
              }).length;

              // Determine button text based on item states and current step type
              const stepVerb = getStepActionVerb();
              const approveButtonText = rejectedItems > 0 || approvedItems > 0
                ? `${stepVerb} Remaining (${pendingItems})` 
                : `${stepVerb} All`;

              const declineButtonText = rejectedItems > 0 || approvedItems > 0
                ? `${getDeclineActionLabel()} Remaining (${pendingItems})`
                : `${getDeclineActionLabel()} All`;

              const hasItemsToApprove = pendingItems > 0;

              return (
                <div className="flex space-x-2">
                  <Button 
                    data-testid="bulk-approve-btn"
                    variant="outline" 
                    className="text-green-600 dark:text-green-400 border-green-600 hover:bg-green-50"
                    onClick={() => {
                      if (orderDetail.currentStepRequireSignature) {
                        openPickupSignatureFlow({ isBulk: true });
                      } else {
                        setShowBulkApprovalModal(true);
                      }
                    }}
                    disabled={isProcessingApproval || !hasItemsToApprove}
                    title={!hasItemsToApprove ? `No pending items to ${getStepActionVerb(false)}` : `${getStepActionVerb()} all pending items`}
                  >
                    <i className="fa-solid fa-check mr-2"></i>
                    {isPollingForStepChange ? 'Waiting for workflow...' : isProcessingApproval ? 'Processing...' : approveButtonText}
                  </Button>
                  <Button 
                    data-testid="bulk-decline-btn"
                    variant="outline" 
                    className="text-red-600 dark:text-red-400 border-red-600 hover:bg-red-50"
                    onClick={() => handleBatchApproval('decline')}
                    disabled={isProcessingApproval || !hasItemsToApprove}
                    title={!hasItemsToApprove ? `No pending items to ${getDeclineActionLabel().toLowerCase()}` : `${getDeclineActionLabel()} all pending items`}
                  >
                    <i className="fa-solid fa-thumbs-down mr-2"></i>
                    {isProcessingApproval ? 'Processing...' : declineButtonText}
                  </Button>
                </div>
              );
            })()}
          </div>
          {orderDetail.currentStepRequireExplicitSubmit && canUserActOnOrder() && Object.keys(draftDecisions).length > 0 && (
            <div role="status" className="mx-6 mb-4 flex items-start gap-2 rounded-md border border-dashed border-amber-400 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
              <i className="fa-solid fa-circle-info mt-0.5"></i>
              <span>
                You have {Object.keys(draftDecisions).length} unsubmitted change{Object.keys(draftDecisions).length !== 1 ? 's' : ''}. Nothing is sent until you click <strong>Submit</strong>.
              </span>
            </div>
          )}
          <div className="p-6 pt-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-secondary-700">
                    <th className="text-left py-3 px-4">Item Details</th>
                    <th className="text-left py-3 px-4">SKU / Part Numbers</th>
                    {!shouldHideVendorColumn() && (
                      <th className="text-left py-3 px-4">Vendor</th>
                    )}
                    <th className="text-right py-3 px-4">Quantity</th>
                    <th className="text-right py-3 px-4">Unit Price</th>
                    <th className="text-right py-3 px-4">Total</th>
                    {canUserActOnOrder() && (
                      <th className="text-center py-3 px-4">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orderDetail.lineItems.map((item, index) => {
                    const itemApprovalStatus = getLineItemApprovalStatus(item);
                    // Highlight rows already approved/accepted, rejected, or quantity-adjusted at the current step so they stand out from pending items
                    const isApprovedRow = itemApprovalStatus === 'approved' && (isCompletedAtCurrentStep(item) || hasCurrentUserDecidedLineItem(item));
                    const isDeclinedRow = itemApprovalStatus === 'rejected';
                    const isQuantityModifiedRow = item.isQuantityModified && item.approvedQuantity != null;
                    const rowDraft = orderDetail.currentStepRequireExplicitSubmit && item.id ? draftDecisions[item.id] : undefined;
                    const isDraftDecline = rowDraft?.decision === 'decline';
                    const draftQuantity = rowDraft?.decision === 'accept' ? rowDraft.approvedQuantity : undefined;
                    const hasDraftQtyChange = draftQuantity != null && draftQuantity !== item.quantity;
                    const rowHighlightClass = rowDraft
                      ? 'bg-amber-50 dark:bg-amber-500/10 outline outline-1 -outline-offset-1 outline-dashed outline-amber-400'
                      : isDeclinedRow
                      ? 'bg-red-50 dark:bg-red-950/30'
                      : isQuantityModifiedRow
                        ? 'bg-yellow-50 dark:bg-yellow-500/10'
                        : isApprovedRow
                          ? 'bg-green-50 dark:bg-green-950/30'
                          : '';
                    return (
                    <tr key={item.id || index} className={`border-b border-gray-100 dark:border-secondary-700 ${rowHighlightClass}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-start">
                          <div className="w-12 h-12 bg-gray-100 dark:bg-secondary-700 rounded-lg mr-3 flex items-center justify-center flex-shrink-0">
                            {item.imageUrl ? (
                              <img 
                                src={item.imageUrl} 
                                alt={item.name}
                                className="w-full h-full object-cover rounded-lg"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            <i className={`fa-solid fa-box text-gray-400 dark:text-secondary-500 ${item.imageUrl ? 'hidden' : ''}`}></i>
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{item.name}</div>
                            {item.description && (
                              <div className="text-xs text-gray-600 dark:text-secondary-400 mt-1">{item.description}</div>
                            )}
                            {item.longDescription && item.longDescription !== item.description && (
                              <div className="text-xs text-gray-500 dark:text-secondary-400 mt-1 max-w-xs truncate" title={item.longDescription}>
                                {item.longDescription}
                              </div>
                            )}
                            {item.unitOfMeasure && (
                              <div className="text-xs text-gray-500 dark:text-secondary-400 mt-1">UOM: {item.unitOfMeasure}</div>
                            )}
                            {item.comment && (
                              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 italic">Note: {item.comment}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          <div className="font-mono text-xs">{formatSkuDisplay(item.sku, item.vendor)}</div>
                          {item.vendorPartNumber && (
                            <div className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                              Vendor P/N: {item.vendorPartNumber}
                            </div>
                          )}
                          {item.altPartNumber && (
                            <div className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                              Alt P/N: {item.altPartNumber}
                            </div>
                          )}
                        </div>
                      </td>
                      {!shouldHideVendorColumn() && (
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {item.vendorName && (
                              <div className="font-medium text-xs">{item.vendorName}</div>
                            )}
                            {item.manufacturerName && (
                              <div className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                                Mfg: {item.manufacturerName}
                              </div>
                            )}
                            {item.taxable !== undefined && (
                              <div className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                                {item.taxable ? 'Taxable' : 'Tax Exempt'}
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="py-3 px-4 text-right">
                        <div className={`font-medium ${(item.approvalStatus?.toLowerCase() === 'rejected' || item.approvalStatus?.toLowerCase() === 'declined') ? 'line-through text-gray-500 dark:text-secondary-400' : ''}`}>
                          {isDraftDecline ? (
                            <div className="flex flex-col items-end">
                              <span className="line-through text-gray-500 dark:text-secondary-400">{item.quantity.toLocaleString()}</span>
                              <span className="text-xs px-1.5 py-0.5 mt-1 rounded bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300">Pending decline</span>
                              {rowDraft?.comments && (
                                <div className="text-xs text-gray-600 dark:text-secondary-400 italic mt-1" title={rowDraft.comments}>
                                  {rowDraft.comments.length > 30 ? rowDraft.comments.substring(0, 30) + '...' : rowDraft.comments}
                                </div>
                              )}
                            </div>
                          ) : hasDraftQtyChange ? (
                            <div className="flex flex-col items-end">
                              <div className="flex items-center space-x-2">
                                <span className="line-through text-gray-500 dark:text-secondary-400">{item.quantity.toLocaleString()}</span>
                                <i className="fa-solid fa-arrow-right text-xs text-gray-400 dark:text-secondary-500"></i>
                                <span className="text-amber-700 dark:text-amber-300 font-semibold">{draftQuantity!.toLocaleString()}</span>
                                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300">
                                  {draftQuantity! > item.quantity ? '+' : ''}{(draftQuantity! - item.quantity).toLocaleString()}
                                </span>
                              </div>
                              <span className="text-xs px-1.5 py-0.5 mt-1 rounded bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300">Pending</span>
                              {rowDraft?.modificationReason && (
                                <div className="text-xs text-gray-600 dark:text-secondary-400 italic mt-1" title={rowDraft.modificationReason}>
                                  {rowDraft.modificationReason.length > 30 ? rowDraft.modificationReason.substring(0, 30) + '...' : rowDraft.modificationReason}
                                </div>
                              )}
                            </div>
                          ) : item.isQuantityModified && item.approvedQuantity != null ? (
                            <div className="flex flex-col items-end"
>
                              <div className="flex items-center space-x-2">
                                <span className="line-through text-gray-500 dark:text-secondary-400">{item.quantity.toLocaleString()}</span>
                                <i className="fa-solid fa-arrow-right text-xs text-gray-400 dark:text-secondary-500"></i>
                                <span className="text-blue-600 dark:text-blue-400 font-semibold">{item.approvedQuantity.toLocaleString()}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  item.approvedQuantity > item.quantity 
                                    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400' 
                                    : 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400'
                                }`}>
                                  {item.approvedQuantity > item.quantity ? '+' : ''}{(item.approvedQuantity - item.quantity).toLocaleString()}
                                </span>
                              </div>
                              {item.modificationReason && (
                                <div className="text-xs text-gray-600 dark:text-secondary-400 italic mt-1" title={item.modificationReason}>
                                  {item.modificationReason.length > 30 ? item.modificationReason.substring(0, 30) + '...' : item.modificationReason}
                                </div>
                              )}
                            </div>
                          ) : (
                            item.quantity.toLocaleString()
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className={`font-medium ${(item.approvalStatus?.toLowerCase() === 'rejected' || item.approvalStatus?.toLowerCase() === 'declined') ? 'line-through text-gray-500 dark:text-secondary-400' : ''}`}>
                          {formatCurrency(item.unitPrice, orderDetail.currency)}
                        </div>
                        {item.fullPrice && item.fullPrice !== item.unitPrice && (
                          <div className="text-xs text-gray-500 dark:text-secondary-400 line-through">
                            {formatCurrency(item.fullPrice, orderDetail.currency)}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {isDraftDecline ? (
                          <div className="font-medium line-through text-gray-500 dark:text-secondary-400">
                            {formatCurrency(item.totalPrice, orderDetail.currency)}
                          </div>
                        ) : hasDraftQtyChange ? (
                          <div className="flex items-center justify-end space-x-2">
                            <span className="line-through text-gray-500 dark:text-secondary-400 font-medium">
                              {formatCurrency(item.totalPrice, orderDetail.currency)}
                            </span>
                            <i className="fa-solid fa-arrow-right text-xs text-gray-400 dark:text-secondary-500"></i>
                            <span className="text-amber-700 dark:text-amber-300 font-semibold">
                              {formatCurrency(item.unitPrice * draftQuantity!, orderDetail.currency)}
                            </span>
                          </div>
                        ) : item.isQuantityModified && item.approvedQuantity != null ? (
                          <div className="flex flex-col items-end">
                            <div className="flex items-center space-x-2">
                              <span className="line-through text-gray-500 dark:text-secondary-400 font-medium">
                                {formatCurrency(item.totalPrice, orderDetail.currency)}
                              </span>
                              <i className="fa-solid fa-arrow-right text-xs text-gray-400 dark:text-secondary-500"></i>
                              <span className="text-blue-600 dark:text-blue-400 font-semibold">
                                {formatCurrency(item.unitPrice * item.approvedQuantity, orderDetail.currency)}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className={`font-medium ${(item.approvalStatus?.toLowerCase() === 'rejected' || item.approvalStatus?.toLowerCase() === 'declined') ? 'line-through text-gray-500 dark:text-secondary-400' : ''}`}>
                            {formatCurrency(item.totalPrice, orderDetail.currency)}
                          </div>
                        )}
                      </td>
                      {canUserActOnOrder() && (
                        <td className="py-3 px-4">
                          {(() => {
                            const draft = orderDetail.currentStepRequireExplicitSubmit && item.id
                              ? draftDecisions[item.id]
                              : undefined;

                            if (draft) {
                              const isQuantityChange = draft.decision === 'accept' && draft.approvedQuantity != null;
                              return (
                                <div className="flex flex-col items-center gap-1">
                                  <div className={`flex items-center px-3 py-1 rounded-full text-sm ${
                                    draft.decision === 'decline'
                                      ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950'
                                      : isQuantityChange
                                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950'
                                        : 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950'
                                  }`}>
                                    <i className={`fa-solid ${draft.decision === 'decline' ? 'fa-xmark' : isQuantityChange ? 'fa-pen-to-square' : 'fa-check'} mr-2`}></i>
                                    Pending {draft.decision === 'decline' ? 'decline' : isQuantityChange ? 'change' : getStepActionVerb(false)}
                                  </div>
                                  <button
                                    type="button"
                                    data-testid={`line-item-undo-${item.id}`}
                                    className="text-xs text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 underline"
                                    onClick={() => item.id && handleUndoDraftDecision(item.id)}
                                  >
                                    <i className="fa-solid fa-rotate-left mr-1"></i>
                                    Undo
                                  </button>
                                </div>
                              );
                            }

                            const approvalStatus = getLineItemApprovalStatus(item);
                            const isResolved = approvalStatus === 'rejected' ||
                              (approvalStatus === 'approved' && (isCompletedAtCurrentStep(item) || hasCurrentUserDecidedLineItem(item)));
                            
                            if (isResolved) {
                              return (
                                <div className="flex items-center justify-center">
                                  {approvalStatus === 'approved' ? (
                                    <div className="flex flex-col items-center">
                                      <div className={`flex items-center px-3 py-1 rounded-full ${
                                        item.isQuantityModified 
                                          ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950' 
                                          : 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950'
                                      }`}>
                                        <i className={`fa-solid ${item.isQuantityModified ? 'fa-pen-to-square' : 'fa-check'} mr-2`}></i>
                                        {item.isQuantityModified ? 'Approved with changes' : 'Approved'}
                                        {item.approverName && (
                                          <span className="ml-2 text-xs opacity-75">by {item.approverName}</span>
                                        )}
                                      </div>
                                      {item.isQuantityModified && item.modificationReason && (
                                        <div className="mt-2 text-xs text-gray-600 dark:text-secondary-400 italic max-w-xs text-center" title={item.modificationReason}>
                                          Reason: {item.modificationReason}
                                        </div>
                                      )}
                                      {item.approvalComments && (
                                        <div className="mt-2 text-xs text-gray-600 dark:text-secondary-400 italic max-w-xs text-center" title={item.approvalComments}>
                                          Comment: {item.approvalComments}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 px-3 py-1 rounded-full">
                                        <i className="fa-solid fa-times mr-2"></i>
                                        Rejected
                                        {item.approverName && (
                                          <span className="ml-2 text-xs opacity-75">by {item.approverName}</span>
                                        )}
                                      </div>
                                      {item.approvalComments && (
                                        <div className="mt-2 text-xs text-gray-600 dark:text-secondary-400 italic max-w-xs text-center">
                                          Reason: {item.approvalComments}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex items-center justify-center space-x-2">
                                  {orderDetail.currentStepCanEditQuantity && (
                                  <button 
                                    className={`p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                      isProcessingApproval ? 'cursor-not-allowed' : ''
                                    }`}
                                    title={isProcessingApproval ? `Processing ${getStepActionVerb(false)}...` : 'Edit quantity'}
                                    onClick={() => handleModifyClick(item.id, orderDetail.currentStep)}
                                    disabled={isProcessingApproval}
                                  >
                                    <i className={`fa-solid ${isProcessingApproval ? 'fa-spinner fa-spin' : 'fa-pen-to-square'}`}></i>
                                  </button>
                                  )}
                                  {!orderDetail.currentStepOrderLevelOnly && (
                                  <>
                                  <button 
                                    data-testid={`line-item-approve-${item.id}`}
                                    className={`p-2 text-green-600 dark:text-green-400 hover:bg-green-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                      isProcessingApproval ? 'cursor-not-allowed' : ''
                                    }`}
                                    title={isProcessingApproval ? `Processing ${getStepActionVerb(false)}...` : `${getStepActionVerb()} this item`}
                                    onClick={() => {
                                      if (orderDetail.currentStepRequireSignature) {
                                        openPickupSignatureFlow({ isBulk: false, lineItemId: item.id });
                                      } else {
                                        handleApprovalAction('accept', item.id, orderDetail.currentStep);
                                      }
                                    }}
                                    disabled={isProcessingApproval}
                                  >
                                    <i className={`fa-solid ${isProcessingApproval ? 'fa-spinner fa-spin' : 'fa-thumbs-up'}`}></i>
                                  </button>
                                  <button 
                                    data-testid={`line-item-reject-${item.id}`}
                                    className={`p-2 text-red-600 dark:text-red-400 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                      isProcessingApproval ? 'cursor-not-allowed' : ''
                                    }`}
                                    title={isProcessingApproval ? `Processing ${getStepActionVerb(false)}...` : 'Reject this item'}
                                    onClick={() => handleApprovalAction('decline', item.id, orderDetail.currentStep)}
                                    disabled={isProcessingApproval}
                                  >
                                    <i className={`fa-solid ${isProcessingApproval ? 'fa-spinner fa-spin' : 'fa-thumbs-down'}`}></i>
                                  </button>
                                  </>
                                  )}
                                </div>
                              );
                            }
                          })()}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 dark:border-secondary-700">
                    <td colSpan={canUserActOnOrder() ? 5 : 4} className="py-4 px-4 text-right font-semibold">
                      Total:
                    </td>
                    <td className="py-4 px-4 text-right font-bold text-lg">
                      {(() => {
                        const adjustedTotal = calculateAdjustedTotal();
                        const originalTotal = calculateOriginalTotal();
                        const hasModifications = orderDetail.lineItems.some(item => 
                          item.isQuantityModified || item.approvalStatus?.toLowerCase() === 'rejected' || item.approvalStatus?.toLowerCase() === 'declined' ||
                          Boolean(orderDetail.currentStepRequireExplicitSubmit && item.id && draftDecisions[item.id])
                        );
                        
                        if (hasModifications && adjustedTotal !== originalTotal) {
                          return (
                            <div className="flex flex-col items-end">
                              <div className="flex items-center space-x-2">
                                <span className="line-through text-gray-500 dark:text-secondary-400 text-base font-normal">
                                  {formatCurrency(originalTotal, orderDetail.currency)}
                                </span>
                                <i className="fa-solid fa-arrow-right text-xs text-gray-400 dark:text-secondary-500"></i>
                                <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">
                                  {formatCurrency(adjustedTotal, orderDetail.currency)}
                                </span>
                              </div>
                            </div>
                          );
                        }
                        return formatCurrency(orderDetail.totalAmount, orderDetail.currency);
                      })()}
                    </td>
                    {canUserActOnOrder() && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          {orderDetail.currentStepRequireExplicitSubmit && canUserActOnOrder() && (() => {
            const actionableItems = orderDetail.lineItems.filter(item => getLineItemApprovalStatus(item) === null);
            if (actionableItems.length === 0) return null;
            const reviewedCount = actionableItems.filter(item => item.id && draftDecisions[item.id]).length;
            const quantityChangeCount = actionableItems.filter(item => item.id && draftDecisions[item.id]?.approvedQuantity != null).length;
            const allReviewed = reviewedCount === actionableItems.length;
            return (
              <div className="border-t border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900/50">
                {showDraftSummary && reviewedCount > 0 && (
                  <div data-testid="draft-summary" className="px-6 pt-4 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-secondary-400 mb-2">Pending changes</p>
                    <ul className="space-y-2">
                      {orderDetail.lineItems.filter(item => item.id && draftDecisions[item.id]).map(item => {
                        const d = draftDecisions[item.id!];
                        const qty = d.decision === 'accept' ? d.approvedQuantity : undefined;
                        const reason = d.decision === 'decline' ? d.comments : d.modificationReason;
                        return (
                          <li key={item.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-gray-700 dark:text-secondary-200">
                            <span className="font-medium">{item.name}</span>
                            {d.decision === 'decline' ? (
                              <span className="text-red-600 dark:text-red-400">Decline ({formatCurrency(item.totalPrice, orderDetail.currency)} removed)</span>
                            ) : qty != null && qty !== item.quantity ? (
                              <span>
                                Qty {item.quantity.toLocaleString()} → <strong>{qty.toLocaleString()}</strong>
                                {' · '}{formatCurrency(item.totalPrice, orderDetail.currency)} → <strong>{formatCurrency(item.unitPrice * qty, orderDetail.currency)}</strong>
                              </span>
                            ) : (
                              <span className="text-green-600 dark:text-green-400">{getStepActionVerb()}</span>
                            )}
                            {reason && <span className="italic text-gray-500 dark:text-secondary-400">Reason: {reason}</span>}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="mt-3 text-gray-700 dark:text-secondary-200">
                      Order total: <span className="line-through text-gray-500 dark:text-secondary-400">{formatCurrency(calculateOriginalTotal(), orderDetail.currency)}</span>
                      {' → '}<strong>{formatCurrency(calculateAdjustedTotal(), orderDetail.currency)}</strong>
                    </p>
                  </div>
                )}
              <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                <div className="text-sm text-gray-600 dark:text-secondary-400">
                  <span className="font-medium text-gray-800 dark:text-secondary-100">{reviewedCount} of {actionableItems.length}</span> items reviewed
                  {quantityChangeCount > 0 && (
                    <span> · {quantityChangeCount} quantity change{quantityChangeCount !== 1 ? 's' : ''}</span>
                  )}
                  {reviewedCount > 0 && (
                    <button
                      type="button"
                      data-testid="toggle-draft-summary"
                      className="ml-3 text-blue-600 dark:text-blue-400 hover:underline"
                      onClick={() => setShowDraftSummary(v => !v)}
                      aria-expanded={showDraftSummary}
                    >
                      {showDraftSummary ? 'Hide changes' : 'View changes'}
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleDiscardDrafts}
                    disabled={reviewedCount === 0 || isProcessingApproval}
                  >
                    Discard Changes
                  </Button>
                  <Button
                    data-testid="submit-review-btn"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleSubmitReview}
                    disabled={!allReviewed || isProcessingApproval}
                    title={!allReviewed ? 'Every item must have a decision before submitting' : 'Submit all reviewed decisions'}
                  >
                    {isProcessingApproval ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </div>
              </div>
            );
          })()}
        </Card>
      </div>

      {/* Rejection Reason Modal */}
      <RejectionReasonModal
        isOpen={rejectionModal.isOpen}
        onClose={handleRejectionCancel}
        onSubmit={handleRejectionSubmit}
        lineItemName={rejectionModal.lineItemName}
        isSubmitting={isProcessingApproval}
        isBulkDecline={rejectionModal.isBulkDecline}
      />

      {/* Pause Reason Modal */}
      <PauseReasonModal
        isOpen={pauseReasonModal.isOpen}
        onClose={() => setPauseReasonModal({ isOpen: false })}
        onSubmit={handlePauseOrderWithReason}
        orderNumber={orderDetail?.orderNumber}
        isSubmitting={pauseOrderMutation.isPending}
        defaultPauseTimeoutHours={
          workflowDetail?.settings?.pauseTimeoutHours ?? 
          divisionData?.settings?.defaultPauseTimeoutHours ?? 
          72
        }
      />

      {/* Quantity Modification Modal */}
      <QuantityModificationModal
        isOpen={modificationModal.isOpen}
        onClose={handleModificationCancel}
        onSubmit={handleModificationSubmit}
        lineItemName={modificationModal.lineItemName}
        originalRequestedQuantity={modificationModal.originalRequestedQuantity}
        currentApprovedQuantity={modificationModal.currentApprovedQuantity}
        unitOfMeasure={modificationModal.unitOfMeasure}
        allowIncrease={orderDetail.currentStepAllowQuantityIncrease ?? false}
        isSubmitting={isProcessingApproval}
      />

      {/* Required Fields Validation Error Modal */}
      {(() => {
        console.log('Rendering modal check - requiredFieldsModal.isOpen:', requiredFieldsModal.isOpen);
        console.log('Full modal state:', requiredFieldsModal);
        return requiredFieldsModal.isOpen;
      })() && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg 
                    className="h-6 w-6 text-yellow-600" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Required Fields Missing
                  </h3>
                  <div className="mt-2 text-sm text-gray-700 dark:text-secondary-200">
                    <p className="mb-3">{requiredFieldsModal.message}</p>
                    {requiredFieldsModal.missingFields && requiredFieldsModal.missingFields.length > 0 && (
                      <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                        <p className="font-medium text-yellow-800 mb-2">
                          Please complete these fields:
                        </p>
                        <ul className="list-disc list-inside space-y-1">
                          {requiredFieldsModal.missingFields.map((field, index) => (
                            <li key={index} className="text-yellow-900">
                              {field}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="mt-3 text-gray-600 dark:text-secondary-400">
                      Scroll down to the <strong>Editable Fields</strong> section to complete the required fields, 
                      then try approving again.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => setRequiredFieldsModal({ isOpen: false })}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Flow Modal */}
      <ApprovalFlowModal
        isOpen={showApprovalFlow}
        onClose={() => setShowApprovalFlow(false)}
        orderDetail={orderDetail}
      />

      {/* Pick List Modal */}
      <PickListModal
        isOpen={showPickList}
        onClose={() => setShowPickList(false)}
        orderDetail={orderDetail}
      />

      {/* Order Audit Report Modal */}
      <OrderAuditModal
        isOpen={showAuditReport}
        onClose={() => setShowAuditReport(false)}
        auditReport={auditReport || null}
        isLoading={isLoadingAuditReport}
      />

      {/* Admin Restart Level Modal */}
      <Modal
        isOpen={showAdminAdvanceModal}
        onClose={() => !adminAdvanceMutation.isPending && setShowAdminAdvanceModal(false)}
        title="Admin: Restart Current Level"
        size="sm"
      >
        <div className="space-y-4">
          {adminAdvanceResult ? (
            <div className={`p-3 rounded-md ${adminAdvanceResult.success ? 'bg-green-50 dark:bg-green-950 text-green-800' : 'bg-red-50 dark:bg-red-950 text-red-800'}`}>
              <p className="text-sm font-medium">{adminAdvanceResult.message}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-secondary-400">
                This will restart <span className="font-semibold">Level {orderDetail?.currentStep}</span> from the beginning:
                approval records will be recreated and notification emails will be re-sent to approvers.
                Existing decisions for this level will be cleared.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full border border-gray-300 dark:border-secondary-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  rows={3}
                  placeholder="Describe why this level needs to be restarted (e.g. approvers never received emails)..."
                  value={adminAdvanceReason}
                  onChange={e => setAdminAdvanceReason(e.target.value)}
                  disabled={adminAdvanceMutation.isPending}
                />
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowAdminAdvanceModal(false)}
              disabled={adminAdvanceMutation.isPending}
            >
              {adminAdvanceResult ? 'Close' : 'Cancel'}
            </Button>
            {!adminAdvanceResult && (
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                disabled={adminAdvanceMutation.isPending || !adminAdvanceReason.trim()}
                onClick={async () => {
                  if (!divisionId || !orderDetail) return;
                  const temporalWorkflowId = `OrderApprovalWorkflow-${orderDetail.id}`;
                  try {
                    const result = await adminAdvanceMutation.mutateAsync({
                      divisionId,
                      temporalWorkflowId,
                      reason: adminAdvanceReason.trim(),
                    });
                    setAdminAdvanceResult({ success: true, message: result.message });
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    await refetch();
                  } catch (err: any) {
                    const errorData = err?.response?.data || err?.data || {};
                    const statusCode = err?.response?.status;
                    const errorCode = errorData?.errorCode;

                    if (statusCode === 409 || errorCode === 'WORKFLOW_ALREADY_RUNNING') {
                      const temporalStatus = errorData?.temporalStatus || 'Running';
                      const temporalRunId = errorData?.temporalRunId;
                      const detailSuffix = temporalRunId
                        ? ` Status: ${temporalStatus}. Run ID: ${temporalRunId}.`
                        : ` Status: ${temporalStatus}.`;

                      setAdminAdvanceResult({
                        success: false,
                        message: `Workflow is already running and cannot be restarted now.${detailSuffix}`,
                      });
                      return;
                    }

                    setAdminAdvanceResult({
                      success: false,
                      message: errorData?.message || err?.message || 'Failed to restart workflow level.',
                    });
                  }
                }}
              >
                {adminAdvanceMutation.isPending ? (
                  <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Restarting...</>
                ) : (
                  <><i className="fa-solid fa-rotate-left mr-2"></i>Restart Level</>
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      {/* Add Approvers Modal */}
      {divisionId && orderDetail && (
        <AddApproversModal
          isOpen={showAddApprovers}
          onClose={() => setShowAddApprovers(false)}
          orderId={orderId || ''}
          divisionId={divisionId}
          currentStep={orderDetail.currentStep || 1}
          totalSteps={orderDetail.totalSteps || 1}
          onSuccess={() => {
            refetch();
            // Show success message
            alert('Approvers added successfully!');
          }}
        />
      )}

      {/* Bulk Approval Confirmation Modal */}
      <Modal
        isOpen={showBulkApprovalModal}
        onClose={() => !isProcessingApproval && setShowBulkApprovalModal(false)}
        title={`${getStepActionVerb()} All Items`}
        data-testid="bulk-approval-modal"
      >
        <div className="space-y-4">
          {isProcessingApproval ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-3"></div>
              <p>Processing {getStepActionVerb(false)} for {(() => {
                const pendingItems = orderDetail?.lineItems.filter(item => {
                  const status = item.approvalStatus?.toLowerCase();
                  return status !== 'rejected' && 
                         status !== 'approved' && 
                         status !== 'accepted';
                }).length || 0;
                return pendingItems;
              })()} items...</p>
              <p className="text-sm text-gray-500 dark:text-secondary-400">Please wait, this may take a moment.</p>
            </div>
          ) : (
            <>
              <p>Are you sure you want to {getStepActionVerb(false)} {(() => {
                const pendingItems = orderDetail?.lineItems.filter(item => {
                  // Use getLineItemApprovalStatus to check if user can act on this item
                  const effectiveStatus = getLineItemApprovalStatus(item);
                  // Only count items where user can take action (effectiveStatus is null)
                  return effectiveStatus === null;
                }).length || 0;
                return pendingItems;
              })()} pending items?</p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                  Comments (Optional)
                </label>
                <textarea
                  data-testid="bulk-approval-comments"
                  value={bulkApprovalComments}
                  onChange={(e) => setBulkApprovalComments(e.target.value)}
                  placeholder="Add comments about your decision..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  rows={3}
                />
              </div>
            </>
          )}
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={() => setShowBulkApprovalModal(false)}
              disabled={isProcessingApproval}
            >
              Cancel
            </Button>
            <Button 
              data-testid="bulk-approval-confirm-btn"
              className="bg-green-600 hover:bg-green-700"
              disabled={isProcessingApproval}
              onClick={() => {
                // Capture before clearing - setBulkApprovalComments('') below won't affect
                // this closure's value, but grab it explicitly to avoid any ambiguity.
                const commentsToSubmit = bulkApprovalComments.trim() || undefined;
                // Close modal immediately to prevent re-rendering with new data
                setShowBulkApprovalModal(false);
                setBulkApprovalComments('');
                // Start batch approval process (doesn't need await since we're closing modal)
                handleBatchApproval('accept', commentsToSubmit);
              }}
            >
              {isProcessingApproval ? 'Processing...' : `${getStepActionVerb()} All`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Pickup Signature Confirmation Modal (Confirm Pickup steps that require a signature) */}
      <PickupSignatureModal
        isOpen={pickupSignatureModal.isOpen}
        onClose={() => !isSubmittingPickupSignature && setPickupSignatureModal({ isOpen: false })}
        onConfirm={handlePickupSignatureConfirm}
        orderNumber={orderDetail?.orderNumber}
        isSubmitting={isSubmittingPickupSignature}
        items={(() => {
          if (!orderDetail) return [];
          if (pickupSignatureModal.isBulk) {
            return orderDetail.lineItems
              .filter(item => getLineItemApprovalStatus(item) === null)
              .map(item => ({ id: item.id!, name: item.name, quantity: item.approvedQuantity ?? item.quantity, unitOfMeasure: item.unitOfMeasure }));
          }
          if (pickupSignatureModal.lineItemId) {
            const item = orderDetail.lineItems.find(li => li.id === pickupSignatureModal.lineItemId);
            // Reflect a pending quantity edit (routed here because this step requires a signature),
            // falling back to a quantity already approved in a prior step, then the original quantity.
            const quantity = pickupSignatureModal.approvedQuantity ?? item?.approvedQuantity ?? item?.quantity;
            return item ? [{ id: item.id!, name: item.name, quantity, unitOfMeasure: item.unitOfMeasure }] : [];
          }
          return [];
        })()}
      />

      {/* Raw Order Data Modal */}
      {/* Delete Order Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => !deleteOrderMutation.isPending && setShowDeleteModal(false)}
        title="Delete Order"
      >
        <div className="p-4">
          <div className="flex items-start gap-3 mb-4">
            <i className="fa-solid fa-triangle-exclamation text-red-500 text-xl mt-0.5"></i>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">This action cannot be undone.</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-secondary-400">
                Order <strong>{orderDetail?.orderNumber}</strong> will be permanently deleted along with all approvals,
                events, and history. Any open Temporal workflow will be terminated.
              </p>
            </div>
          </div>
          {deleteError && (
            <p className="mb-3 text-sm text-red-600 dark:text-red-400">{deleteError}</p>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteOrderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
              disabled={deleteOrderMutation.isPending}
              onClick={async () => {
                if (!divisionId || !orderId) return;
                setDeleteError(null);
                try {
                  await deleteOrderMutation.mutateAsync({ divisionId, orderId });
                  setShowDeleteModal(false);
                  navigate(-1);
                } catch (err: any) {
                  setDeleteError(err?.response?.data?.message || 'Failed to delete order. Please try again.');
                }
              }}
            >
              {deleteOrderMutation.isPending ? 'Deleting...' : 'Delete Order'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Raw Order Data Modal */}
      {showRawData && (
        <div className="fixed inset-0 z-50 overflow-y-auto focus:outline-none" aria-labelledby="modal-title" role="dialog" aria-modal="true" ref={rawDataDialogRef} tabIndex={-1}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
              aria-hidden="true"
              onClick={() => setShowRawData(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-secondary-800 rounded-lg shadow-xl">
              {/* Header */}
              <div className="px-6 py-4 bg-gray-50 dark:bg-secondary-900 border-b border-gray-200 dark:border-secondary-700 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" id="modal-title">
                  Raw Order Data - {orderDetail.orderNumber}
                </h3>
                <button
                  onClick={() => setShowRawData(false)}
                  className="text-gray-400 dark:text-secondary-500 hover:text-gray-500"
                >
                  <i className="fa-solid fa-xmark text-xl"></i>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                <div className="mb-4">
                  <button
                    onClick={() => {
                      const dataStr = orderDetail.orderDocument 
                        ? JSON.stringify(orderDetail.orderDocument, null, 2)
                        : 'No order document available';
                      navigator.clipboard.writeText(dataStr);
                    }}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700"
                  >
                    <i className="fa-solid fa-copy mr-2"></i>
                    Copy to Clipboard
                  </button>
                </div>
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                  {orderDetail.orderDocument 
                    ? JSON.stringify(orderDetail.orderDocument, null, 2)
                    : 'No order document available'}
                </pre>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 dark:bg-secondary-900 border-t border-gray-200 dark:border-secondary-700 flex justify-end">
                <button
                  onClick={() => setShowRawData(false)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
}